import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isSignInWithEmailLink, onAuthStateChanged, signOut } from "firebase/auth";
import { arrayRemove, collection, deleteDoc, doc, getDoc, getDocFromCache, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore";
import useMediaRecorder from "./hooks/useMediaRecorder";
import { C, setTheme } from "./theme";
import BottomNav from "./components/BottomNav";
import Button from "./components/Button";
import PromoBanner from "./components/PromoBanner";
import SupportButton, { MailIcon } from "./components/SupportButton";
import SupportModal from "./components/SupportModal";
import WhatsNewModal from "./components/WhatsNewModal";
import CacheStatusModal from "./components/CacheStatusModal";
import { getUnseenEntries } from "./data/changelog";
import { APP_VERSION } from "./appVersion";
import { captureScreenshot } from "./services/support";
import AuthScreen from "./screens/AuthScreen";
import ActivationScreen from "./screens/ActivationScreen";
import VerifyEmailScreen from "./screens/VerifyEmailScreen";
import EnrollmentScreen from "./screens/EnrollmentScreen";
import DashboardScreen from "./screens/DashboardScreen";
import CachedScreen from "./screens/CachedScreen";
import TournamentScreen from "./screens/TournamentScreen";
import RecordingScreen from "./screens/RecordingScreen";
import ReportScreen from "./screens/ReportScreen";
import SessionsScreen from "./screens/SessionsScreen";
import PatternsScreen from "./screens/PatternsScreen";
import CatchScreen from "./screens/CatchScreen";
import VideoScreen from "./screens/VideoScreen";
import GoProScreen from "./screens/GoProScreen";
import TrimScreen from "./screens/TrimScreen";
import WelcomeScreen from "./screens/WelcomeScreen";
import useGeolocation from "./hooks/useGeolocation";
import useNetworkStatus from "./hooks/useNetworkStatus";
import useOfflineMode from "./hooks/useOfflineMode";
import useOfflineSync from "./hooks/useOfflineSync";
import { SessionProvider } from "./context/SessionContext";
import { auth, db } from "./firebase";
import { sendTournamentGeofenceAlert } from "./services/tournamentAlerts";
import { buildTournamentEventNotification, showTournamentNativeNotification } from "./services/tournamentEventNotifications";
import {
  primeTournamentAlertAudio,
  triggerTournamentAlertFeedback,
} from "./services/tournamentAlertFeedback";
import { claimActiveDeviceSession, watchActiveDeviceSession } from "./services/deviceSession";
import { maybePromptForReview } from "./services/appReview";
import { hasTournamentAreaRestriction, isLocationInsideGeofence, normalizeTournamentGeofence } from "./utils/geofence";
import { authedRead, authedWrite } from "./utils/authedWrite";
import {
  capturePromoFromUrl,
  getAttribution,
  hasSeenBanner,
  markBannerSeen,
  getPendingBookToken,
  clearPendingBookToken,
} from "./utils/promo";
import { redeemBookToken } from "./services/bookRedeem";

// Capture ?promo=xxx into localStorage as early as possible (module-load),
// so the attribution survives the OAuth redirect roundtrip that strips the
// URL's query string.
capturePromoFromUrl();

// Email-verification gate: accounts created on/after this UTC timestamp
// must verify their email before accessing the app. Earlier accounts are
// grandfathered to avoid locking out existing users.
const EMAIL_VERIFICATION_GATE_CUTOFF_MS = Date.parse("2026-05-29T00:00:00Z");

// How often to append a GPS breadcrumb to the active tournament session, and
// the minimum distance a boat must move before a new point is worth recording
// (holding on a spot shouldn't spam Firestore writes).
const TRAIL_INTERVAL_MS = 30000;
const TRAIL_MIN_MOVE_METERS = 5;
const TRAIL_MAX_GAP_MS = 120000;

// Rough great-circle distance in meters between two {lat,lng} points. Used to
// skip near-duplicate GPS breadcrumbs when a boat is stationary.
function trailMetersBetween(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function NoticeCountdown({ targetMs, onExpire }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const remaining = Math.max(0, Math.floor((targetMs - now) / 1000));
  useEffect(() => {
    if (remaining <= 0 && typeof onExpire === "function") {
      onExpire();
    }
  }, [remaining, onExpire]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 800 }}>
      {mm}:{ss}
    </span>
  );
}


// Tournament docs may carry their eligible-species list under a few different
// field names depending on which tool wrote them (legacy `fishSpecies`, or the
// desktop Tournament Builder). Read the first non-empty candidate and normalize
// each entry (string or object) to a display name so the Catch Log can restrict
// the species picker to exactly what the tournament allows.
const TOURNAMENT_SPECIES_FIELDS = [
  "fishSpecies",
  "species",
  "speciesList",
  "eligibleSpecies",
  "allowedSpecies",
  "targetSpecies",
];

function extractTournamentSpecies(doc) {
  if (!doc || typeof doc !== "object") return [];
  const out = [];
  const seen = new Set();
  const push = (entry) => {
    let name = "";
    if (typeof entry === "string") name = entry;
    else if (entry && typeof entry === "object") {
      name = entry.name || entry.label || entry.species || entry.commonName || entry.value || entry.id || "";
    }
    name = String(name || "").trim();
    const key = name.toLowerCase();
    if (name && !seen.has(key)) {
      seen.add(key);
      out.push(name);
    }
  };
  for (const field of TOURNAMENT_SPECIES_FIELDS) {
    const arr = doc[field];
    if (Array.isArray(arr) && arr.length > 0) {
      arr.forEach(push);
      if (out.length > 0) return out;
    }
  }
  return out;
}


function App() {
  const normalizeTournamentKey = (value = "") =>
    String(value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isActivationLink, setIsActivationLink] = useState(() => {
    try {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        // Persist the full activation URL immediately so the ActivationScreen
        // can complete sign-in even if the URL bar gets cleaned (router/promo
        // capture, etc.) before the user finishes creating their password.
        try { localStorage.setItem("the99.activationLink", window.location.href); } catch {}
        return true;
      }
      return false;
    } catch {
      return false;
    }
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [loadedProfileUid, setLoadedProfileUid] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileAccessError, setProfileAccessError] = useState("");
  const [leavingTournament, setLeavingTournament] = useState(false);
  const [isMobileWidth, setIsMobileWidth] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth <= 480;
  });
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === "undefined") return 800;
    return Math.round(window.visualViewport?.height || window.innerHeight || 800);
  });
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [screen, setScreen] = useState("dashboard");
  const [showCatch, setShowCatch] = useState(false);
  // Which outcome the catch sheet opens in — "landed" (normal) or "missed"
  // (a bite that got away, opened via RecordingScreen's "Missed fish" button).
  const [catchInitialOutcome, setCatchInitialOutcome] = useState("landed");
  const [showVideo, setShowVideo] = useState(false);
  const [pendingCertId, setPendingCertId] = useState(null);
  // Trim step shown between a GoPro clip download and Confirm & Certify.
  const [showTrim, setShowTrim] = useState(false);
  const [trimTarget, setTrimTarget] = useState(null);
  // Catch logged from the GoPro screen right after recording (before the clip is
  // downloaded/certified). Staged here until certify hand-off attaches it.
  const [goproPendingCatch, setGoproPendingCatch] = useState(null);
  // True while CatchScreen is open in "log first" mode for the GoPro flow, so
  // its onSaved result is captured into goproPendingCatch.
  const [catchFirstMode, setCatchFirstMode] = useState(false);
  // Catch resolved for a GoPro clip after the certify/catch flow — handed back to
  // GoProScreen so the clip list shows the summary immediately (keyed by clipKey).
  const [goproClipCatch, setGoproClipCatch] = useState(null);
  // Clip key ("folder/name") currently moving through the certify/catch flow.
  const [activeGoproClipKey, setActiveGoproClipKey] = useState(null);
  const [hasStartedTrip, setHasStartedTrip] = useState(false);
  // Set when a scheduled-trip notification's "Start session" action (or a tap on
  // the T-0 banner) fires, so the dashboard auto-starts the session on open.
  const [pendingAutoStart, setPendingAutoStart] = useState(null);
  const [reportSessionId, setReportSessionId] = useState(null);  const [reportHighlightCatchId, setReportHighlightCatchId] = useState(null);
  const [pendingDeepLink, setPendingDeepLink] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const params = new URLSearchParams(window.location.search);
      const screenParam = (params.get("screen") || "").trim().toLowerCase();
      if (screenParam !== "report") return null;
      return {
        screen: "report",
        sessionId: (params.get("session") || "").trim() || null,
        catchId: (params.get("catch") || "").trim() || null,
      };
    } catch {
      return null;
    }
  });
  const [videoTarget, setVideoTarget] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  // Wall-clock timestamp (ms) when the active trip started. The session timer is
  // derived from this rather than a per-second tick counter, so it keeps the
  // correct elapsed time even when the user navigates away from the recording
  // screen and back (the old counter reset to 0 on unmount). `null` when no
  // trip is running.
  const [sessionStartMs, setSessionStartMs] = useState(null);
  // True when the active trip was rehydrated from a previous login/session
  // (app reload mid-trip, or async orphan recovery) rather than freshly
  // started. Drives a "resumed" label next to the recording-screen timer.
  const [sessionResumed, setSessionResumed] = useState(false);
  const [activeTournamentGeofence, setActiveTournamentGeofence] = useState(null);
  const [activeTournamentDates, setActiveTournamentDates] = useState(null);
  const [activeTournamentStatus, setActiveTournamentStatus] = useState("");
  const [activeTournamentDocId, setActiveTournamentDocId] = useState("");
  const [activeTournamentFishSpecies, setActiveTournamentFishSpecies] = useState([]);
  const [tournamentLiveMap, setTournamentLiveMap] = useState({});
  const [inferredTournamentAssignment, setInferredTournamentAssignment] = useState(null);
  const [tournamentNotice, setTournamentNotice] = useState(null);
  const [tournamentNoticeQueue, setTournamentNoticeQueue] = useState([]);
  const [deviceEvictedNotice, setDeviceEvictedNotice] = useState(null);
  // One-time welcome card. Shown the first time an angler reaches the app after
  // signing up + logging in, then never again on this install (persisted flag).
  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      return window.localStorage.getItem("the99.hasSeenWelcome") !== "true";
    } catch {
      return false;
    }
  });
  const dismissWelcome = useCallback(() => {
    try {
      window.localStorage.setItem("the99.hasSeenWelcome", "true");
    } catch {
      /* non-fatal */
    }
    setShowWelcome(false);
  }, []);
  const [promoAttribution] = useState(() => getAttribution());
  const [promoBannerDismissed, setPromoBannerDismissed] = useState(() =>
    promoAttribution ? hasSeenBanner(promoAttribution.source) : true
  );
  const autoRoutedUidRef = useRef(null);
  const launchGeofenceEventRef = useRef("");
  const launchGeofenceInsideRef = useRef(null);
  const userProfileFallbackRef = useRef({ tournamentName: "", teamName: "" });
  const processedTournamentEventIdsRef = useRef(new Set());
  const processingTournamentEventIdsRef = useRef(new Set());
  const processedTeamEventIdsRef = useRef(new Set());
  const processingTeamEventIdsRef = useRef(new Set());
  const processedUserEventIdsRef = useRef(new Set());
  const processingUserEventIdsRef = useRef(new Set());
  const activeTournamentEndMsRef = useRef(null);
  const soundAlertsRef = useRef(true);
  const vibrationAlertsRef = useRef(true);
  const userProfileRef = useRef(null);
  const timerRef = useRef(null);
  const profileLoadTimeoutRef = useRef(null);
  const isRecording = screen === "recording";

  const readBooleanFromStorage = (key, fallback) => {
    try {
      const raw = localStorage.getItem(key);
      return raw == null ? fallback : JSON.parse(raw);
    } catch {
      return fallback;
    }
  };

  const [showGps, setShowGps] = useState(() => readBooleanFromStorage("the99.showGps", false));
  const [showConditions, setShowConditions] = useState(() => readBooleanFromStorage("the99.showConditions", true));
  const [showGoProButton, setShowGoProButton] = useState(() => readBooleanFromStorage("the99.showGoProButton", false));
  // Max GoPro clip length in minutes before auto-stop (with a 30s warning).
  // Default 5, configurable 1–10 in User Preferences.
  const [goProMaxClipMins, setGoProMaxClipMins] = useState(() => {
    try {
      const raw = parseInt(localStorage.getItem("the99.goProMaxClipMins"), 10);
      if (Number.isFinite(raw)) return Math.min(10, Math.max(1, raw));
    } catch {}
    return 5;
  });
  const [soundAlerts, setSoundAlerts] = useState(() => readBooleanFromStorage("the99.soundAlerts", true));
  const [vibrationAlerts, setVibrationAlerts] = useState(() => readBooleanFromStorage("the99.vibrationAlerts", true));
  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem("the99.themeMode") === "light" ? "light" : "dark";
    } catch {
      return "dark";
    }
  });
  const [fontSizeMode, setFontSizeMode] = useState(() => {
    try {
      return localStorage.getItem("the99.fontSizeMode") === "large" ? "large" : "normal";
    } catch {
      return "normal";
    }
  });
  const { position: location, status: geoStatus, requestPermission: requestGeoPermission } = useGeolocation({ enabled: true });
  const recorder = useMediaRecorder();
  const isOnline = useNetworkStatus();
  const { forceOffline, setForceOffline } = useOfflineMode();
  const isOffline = !isOnline || forceOffline;
  // "Fishing today" selection for anglers who fish BOTH inshore and offshore.
  // Offshore trips default to Cached (Offline) Mode and swap in the offshore
  // backdrop. Prompted once per calendar day; reset on logout.
  const [offshoreToday, setOffshoreToday] = useState(false);
  const [fishingDayPrompt, setFishingDayPrompt] = useState(null); // null | "choose" | "cached"
  const fishingDayCheckedRef = useRef("");
  const [uploadSettings, setUploadSettings] = useState({
    uploadOverCellular: true,
    deleteAfterUpload: true,
    autoUpload: true,
  });
  const { pendingVideoCount, pendingCatchCount, pendingGeofenceAlertCount, syncing, lastSyncAt, items: cacheItems, activeUpload, retryNow, removeItem } = useOfflineSync({ isOffline, uploadSettings });
  setTheme(themeMode);
  const toggleThemeMode = () => setThemeMode((prev) => (prev === "light" ? "dark" : "light"));

  // Offline-cache upload settings, synced with Firestore userPreferences.
  useEffect(() => {
    const uid = authUser?.uid;
    if (!uid) return undefined;
    const unsub = onSnapshot(
      doc(db, "userPreferences", uid),
      (snap) => {
        const data = snap.exists() ? snap.data() || {} : {};
        setUploadSettings({
          uploadOverCellular: data.uploadOverCellular !== false,
          deleteAfterUpload: data.deleteAfterUpload !== false,
          autoUpload: data.autoUpload !== false,
        });
      },
      (e) => console.warn("[App] failed to load upload settings:", e?.message || e)
    );
    return () => unsub();
  }, [authUser?.uid]);

  const handleChangeUploadSetting = useCallback(async (key, value) => {
    setUploadSettings((prev) => ({ ...prev, [key]: value }));
    const uid = authUser?.uid;
    if (!uid) return;
    try {
      await setDoc(doc(db, "userPreferences", uid), { [key]: value }, { merge: true });
    } catch (e) {
      console.warn("[App] failed to save upload setting:", e?.message || e);
    }
  }, [authUser?.uid]);

  useEffect(() => {
    let cancelled = false;

    const inferTournamentAssignment = async () => {
      if (!authUser?.uid || !userProfile) {
        setInferredTournamentAssignment(null);
        return;
      }

      if (userProfile?.tournamentName && userProfile?.tournamentId && userProfile?.teamId && userProfile?.teamName) {
        setInferredTournamentAssignment(null);
        return;
      }

      try {
        let inferred = null;
        let inferredTeam = null;

        // Gather candidate team docs via multiple signals so we don't miss the team
        // when the user is listed as captain, or when membership was recorded on the
        // user profile (`teamId`) but not echoed into `memberUserIds`.
        const teamDocsById = new Map();
        const ingestTeamDocs = (snapOrDocs) => {
          const docs = Array.isArray(snapOrDocs) ? snapOrDocs : snapOrDocs?.docs || [];
          docs.forEach((d) => {
            if (d?.exists?.() && !teamDocsById.has(d.id)) {
              teamDocsById.set(d.id, d);
            }
          });
        };

        try {
          const memberSnap = await authedRead(
            () =>
              getDocs(
                query(collection(db, "teams"), where("memberUserIds", "array-contains", authUser.uid))
              ),
            "teams.memberUserIds"
          );
          ingestTeamDocs(memberSnap);
        } catch (qErr) {
          console.warn("[App] teams memberUserIds query failed:", qErr);
        }

        try {
          const captainSnap = await authedRead(
            () =>
              getDocs(
                query(collection(db, "teams"), where("captainUserId", "==", authUser.uid))
              ),
            "teams.captainUserId"
          );
          ingestTeamDocs(captainSnap);
        } catch (qErr) {
          console.warn("[App] teams captainUserId query failed:", qErr);
        }

        const profileTeamId = String(userProfile?.teamId || "").trim();
        if (profileTeamId && !teamDocsById.has(profileTeamId)) {
          try {
            const directTeamDoc = await authedRead(
              () => getDoc(doc(db, "teams", profileTeamId)),
              "teams.direct"
            );
            if (directTeamDoc.exists()) {
              teamDocsById.set(directTeamDoc.id, directTeamDoc);
            }
          } catch (qErr) {
            console.warn("[App] direct team fetch failed:", qErr);
          }
        }

        const teamSnap = { empty: teamDocsById.size === 0, docs: Array.from(teamDocsById.values()) };

        if (!teamSnap.empty) {
          for (const teamDoc of teamSnap.docs) {
            const teamData = teamDoc.data() || {};
            const candidateTeamName = String(teamData.teamName || "").trim();
            const candidateTeamId = String(teamData.teamId || teamDoc.id || "").trim();
            if (!inferredTeam && (candidateTeamName || candidateTeamId)) {
              inferredTeam = {
                teamName: candidateTeamName,
                teamId: candidateTeamId,
              };
            }
            const candidateTournamentName = String(teamData.tournamentName || "").trim();
            const candidateTournamentId = String(teamData.tournamentId || "").trim();
            if (candidateTournamentName || candidateTournamentId) {
              inferred = {
                tournamentName: candidateTournamentName,
                tournamentId: candidateTournamentId,
                teamName: candidateTeamName,
                teamId: candidateTeamId,
              };
              break;
            }
          }
        }

        // Fallback: read team assignment events if the team doc itself doesn't carry
        // tournament fields. The admin app writes `team_assigned_to_tournament` events
        // with metadata.tournamentId / metadata.tournamentName into teams/{teamId}/events.
        if (!inferred && inferredTeam?.teamId) {
          try {
            const assignmentEventsSnap = await authedRead(
              () => getDocs(collection(db, "teams", inferredTeam.teamId, "events")),
              "teams.events"
            );
            let latestAssignment = null;
            let latestAssignmentMs = 0;
            assignmentEventsSnap.docs.forEach((evDoc) => {
              const evData = evDoc.data() || {};
              const evType = String(evData.type || evData.eventType || "").trim();
              if (evType !== "team_assigned_to_tournament") return;
              const metadata = evData.metadata || {};
              const t = metadata.tournamentId || metadata.tournamentName
                ? {
                    tournamentId: String(metadata.tournamentId || "").trim(),
                    tournamentName: String(metadata.tournamentName || "").trim(),
                  }
                : null;
              if (!t) return;
              const createdAt = evData.createdAt;
              const ms = (() => {
                if (!createdAt) return 0;
                if (typeof createdAt.toMillis === "function") return createdAt.toMillis();
                if (typeof createdAt.seconds === "number") return createdAt.seconds * 1000;
                const d = new Date(createdAt);
                return Number.isNaN(d.getTime()) ? 0 : d.getTime();
              })();
              if (ms >= latestAssignmentMs) {
                latestAssignmentMs = ms;
                latestAssignment = t;
              }
            });
            if (latestAssignment) {
              inferred = {
                tournamentName: latestAssignment.tournamentName,
                tournamentId: latestAssignment.tournamentId,
                teamName: inferredTeam.teamName,
                teamId: inferredTeam.teamId,
              };
              console.info("[App] inferred tournament assignment from team event:", inferred);
            }
          } catch (eventError) {
            console.warn("[App] could not read team assignment events:", eventError);
          }
        }

        if (!inferred) {
          const tournamentsSnap = await authedRead(
            () => getDocs(collection(db, "tournaments")),
            "tournaments"
          );
          if (!tournamentsSnap.empty) {
            let matchedTournamentDoc = null;

            tournamentsSnap.docs.forEach((tDoc) => {
              if (matchedTournamentDoc) return;
              const data = tDoc.data() || {};
              const directorId = String(data.directorId || "").trim();
              const createdByUid = String(data.createdByUid || "").trim();
              const directorEmail = String(data.directorEmail || "").trim().toLowerCase();
              const authEmail = String(authUser?.email || userProfile?.email || "").trim().toLowerCase();

              if ((directorId && directorId === authUser.uid) || (createdByUid && createdByUid === authUser.uid)) {
                matchedTournamentDoc = tDoc;
                return;
              }

              if (authEmail && directorEmail && directorEmail === authEmail) {
                matchedTournamentDoc = tDoc;
              }
            });

            if (!matchedTournamentDoc && tournamentsSnap.size === 1) {
              matchedTournamentDoc = tournamentsSnap.docs[0];
            }

            if (matchedTournamentDoc) {
              const data = matchedTournamentDoc.data() || {};
              const candidateTournamentName = String(data.name || "").trim();
              inferred = {
                tournamentName: candidateTournamentName,
                // Contract: tournamentId MUST be the tournament's Firestore doc
                // id (never a name slug) so it matches the dashboard's writes
                // and its collectionGroup queries.
                tournamentId: matchedTournamentDoc.id,
                teamName: String(userProfile?.teamName || inferredTeam?.teamName || "").trim(),
                teamId: String(userProfile?.teamId || inferredTeam?.teamId || "").trim(),
              };
            }
          }
        }

        if (!inferred && inferredTeam) {
            inferred = {
              tournamentName: "",
              tournamentId: "",
              teamName: String(userProfile?.teamName || inferredTeam.teamName || "").trim(),
              teamId: String(userProfile?.teamId || inferredTeam.teamId || "").trim(),
            };
          }

        if (!cancelled) {
          if (inferred) {
            console.info("[App] tournament assignment resolved:", inferred);
          } else {
            console.info("[App] no tournament assignment could be inferred for uid:", authUser.uid);
          }
          setInferredTournamentAssignment(inferred);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("[App] could not infer tournament assignment:", error);
          setInferredTournamentAssignment(null);
        }
      }
    };

    inferTournamentAssignment();

    return () => {
      cancelled = true;
    };
  }, [authUser?.uid, authUser?.email, userProfile]);

  const effectiveUserProfile = React.useMemo(() => {
    if (!userProfile) return userProfile;

    // Normalize the dashboard's `tournament_director` role to the canonical
    // `director` value the mobile app uses everywhere. The dashboard
    // onboarding writes the longer form; without this coercion, every
    // `role === "director"` check (routing, gating, tournament screen
    // selectors) silently misses TDs.
    const rawRole = String(userProfile.role || "");
    const normalizedRole = rawRole === "tournament_director" ? "director" : rawRole;
    const baseProfile = normalizedRole !== rawRole
      ? { ...userProfile, role: normalizedRole }
      : userProfile;

    if (!inferredTournamentAssignment) return baseProfile;

    const tournamentName = baseProfile.tournamentName || inferredTournamentAssignment.tournamentName || "";
    const tournamentId = baseProfile.tournamentId || inferredTournamentAssignment.tournamentId || "";
    const teamName = baseProfile.teamName || inferredTournamentAssignment.teamName || "";
    const teamId = baseProfile.teamId || inferredTournamentAssignment.teamId || "";

    if (
      tournamentName === (baseProfile.tournamentName || "") &&
      tournamentId === (baseProfile.tournamentId || "") &&
      teamName === (baseProfile.teamName || "") &&
      teamId === (baseProfile.teamId || "")
    ) {
      return baseProfile;
    }

    return {
      ...baseProfile,
      tournamentName,
      tournamentId,
      teamName,
      teamId,
      inTournament: Boolean(baseProfile.inTournament || tournamentName || tournamentId),
    };
  }, [inferredTournamentAssignment, userProfile]);

  useEffect(() => {
    if (!effectiveUserProfile?.tournamentName && !effectiveUserProfile?.tournamentId) {
      setActiveTournamentGeofence(null);
      setActiveTournamentDates(null);
      setActiveTournamentStatus("");
      setActiveTournamentDocId("");
      setActiveTournamentFishSpecies([]);
      return undefined;
    }

    const targetTournamentId = String(effectiveUserProfile?.tournamentId || "").trim().toLowerCase();
    const targetTournamentName = String(effectiveUserProfile?.tournamentName || "").trim().toLowerCase();
    const targetTournamentIdKey = normalizeTournamentKey(targetTournamentId);
    const targetTournamentNameKey = normalizeTournamentKey(targetTournamentName);

    const unsub = onSnapshot(
      collection(db, "tournaments"),
      (snap) => {
        let matched = null;
        snap.docs.forEach((item) => {
          if (matched) return;
          const data = item.data() || {};
          const idKey = String(item.id || "").trim().toLowerCase();
          const nameKey = String(data.name || "").trim().toLowerCase();
          const slugKey = normalizeTournamentKey(
            data.tournamentId || data.slug || data.name || item.id || ""
          );
          if (
            (targetTournamentId && idKey === targetTournamentId) ||
            (targetTournamentName && nameKey === targetTournamentName) ||
            (targetTournamentIdKey && slugKey === targetTournamentIdKey) ||
            (targetTournamentNameKey && slugKey === targetTournamentNameKey)
          ) {
            matched = { id: item.id, ...(data || {}) };
          }
        });

        setActiveTournamentGeofence(normalizeTournamentGeofence(matched));
        setActiveTournamentDocId(String(matched?.id || "").trim());
        setActiveTournamentStatus(
          String(matched?.status || matched?.tournamentStatus || "").trim().toLowerCase()
        );
        setActiveTournamentFishSpecies(extractTournamentSpecies(matched));

        if (!matched) {
          console.info("[App] no tournaments/* doc matched profile assignment:", {
            profileTournamentId: targetTournamentId,
            profileTournamentName: targetTournamentName,
          });
        } else {
          console.info("[App] matched tournament doc:", {
            id: matched.id,
            name: matched.name,
            status: matched.status || matched.tournamentStatus || "",
          });
        }

        const toMs = (v) => {
          if (!v) return null;
          if (typeof v?.toMillis === "function") return v.toMillis();
          if (typeof v?.seconds === "number") return v.seconds * 1000;
          const d = new Date(v);
          return Number.isNaN(d.getTime()) ? null : d.getTime();
        };
        const startMs = matched ? toMs(matched.startDate) : null;
        const endMs = matched ? toMs(matched.endDate) : null;
        setActiveTournamentDates(startMs || endMs ? { startDate: startMs, endDate: endMs } : null);
      },
      () => { setActiveTournamentGeofence(null); setActiveTournamentDates(null); setActiveTournamentStatus(""); setActiveTournamentDocId(""); setActiveTournamentFishSpecies([]); }
    );

    return () => unsub();
  }, [effectiveUserProfile?.tournamentId, effectiveUserProfile?.tournamentName]);

  const tournamentAreaRestricted =
    hasTournamentAreaRestriction(activeTournamentGeofence) &&
    activeTournamentStatus !== "archived" &&
    activeTournamentStatus !== "ended";
  const effectiveProfileLoading = profileLoading || (authUser?.uid != null && loadedProfileUid !== authUser.uid);

  useEffect(() => {
    activeTournamentEndMsRef.current =
      typeof activeTournamentDates?.endDate === "number" ? activeTournamentDates.endDate : null;
  }, [activeTournamentDates?.endDate]);

  useEffect(() => {
    soundAlertsRef.current = !!soundAlerts;
  }, [soundAlerts]);

  useEffect(() => {
    vibrationAlertsRef.current = !!vibrationAlerts;
  }, [vibrationAlerts]);

  useEffect(() => {
    processedTournamentEventIdsRef.current = new Set();
    processingTournamentEventIdsRef.current = new Set();
  }, [activeTournamentDocId]);

  useEffect(() => {
    setTournamentNotice(null);
    setTournamentNoticeQueue([]);
  }, [authUser?.uid]);

  useEffect(() => {
    processedTeamEventIdsRef.current = new Set();
    processingTeamEventIdsRef.current = new Set();
  }, [effectiveUserProfile?.teamId, authUser?.uid]);

  useEffect(() => {
    processedUserEventIdsRef.current = new Set();
    processingUserEventIdsRef.current = new Set();
  }, [authUser?.uid]);

  useEffect(() => {
    if (tournamentNotice || tournamentNoticeQueue.length === 0) {
      return;
    }

    const [nextNotice, ...remaining] = tournamentNoticeQueue;
    setTournamentNotice(nextNotice);
    setTournamentNoticeQueue(remaining);
  }, [tournamentNotice, tournamentNoticeQueue]);

  useEffect(() => {
    if (!tournamentNotice) {
      return undefined;
    }

    // Notices with a live countdown stay until the timer expires (handled by
    // the banner UI) or the user dismisses; don't auto-hide them.
    if (tournamentNotice.countdownToMs) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setTournamentNotice(null);
    }, 9000);

    return () => window.clearTimeout(timeoutId);
  }, [tournamentNotice]);

  useEffect(() => {
    if (!authUser?.uid || !activeTournamentDocId || effectiveProfileLoading) {
      return undefined;
    }

    const listenerAttachedAtMs = Date.now();

    const eventsQuery = query(
      collection(db, "tournaments", activeTournamentDocId, "events"),
      where("status", "==", "pending")
    );

    const unsub = onSnapshot(
      eventsQuery,
      (snap) => {
        for (const item of snap.docs) {
          const eventId = String(item.id || "").trim();
          if (!eventId) continue;
          if (processedTournamentEventIdsRef.current.has(eventId) || processingTournamentEventIdsRef.current.has(eventId)) {
            continue;
          }

          processingTournamentEventIdsRef.current.add(eventId);

          (async () => {
            try {
              const eventData = item.data() || {};
              const currentRole = String(effectiveUserProfile?.role || "").trim().toLowerCase();
              const targetRole = String(eventData.targetRole || "").trim().toLowerCase();
              const targetUserId = String(eventData.targetUserId || "").trim();

              if ((targetUserId && targetUserId !== authUser.uid) || (targetRole && targetRole !== currentRole)) {
                processedTournamentEventIdsRef.current.add(eventId);
                return;
              }

              const rawEventType = String(eventData.type || eventData.eventType || "").trim();
              const tournamentEndMs = activeTournamentEndMsRef.current;
              const tournamentHasEnded =
                typeof tournamentEndMs === "number" && tournamentEndMs > 0 && Date.now() > tournamentEndMs;

              // Suppress stale time-based reminders once the tournament is over (or archived).
              // Ack them so they don't re-fire on subsequent logins.
              if (
                tournamentHasEnded &&
                (rawEventType === "tournament_starting_soon" ||
                  rawEventType === "tournament_ending_soon" ||
                  rawEventType === "tournament_before_start" ||
                  rawEventType === "tournament_before_end")
              ) {
                try {
                  const ackRef = doc(
                    db,
                    "tournaments",
                    activeTournamentDocId,
                    "events",
                    eventId,
                    "sentToAnglers",
                    authUser.uid
                  );
                  await setDoc(
                    ackRef,
                    {
                      anglerId: authUser.uid,
                      eventId,
                      eventType: rawEventType,
                      tournamentId: activeTournamentDocId,
                      sentAt: serverTimestamp(),
                      channel: "suppressed-stale",
                      notificationPermission: "n/a",
                    },
                    { merge: true }
                  );
                } catch (ackError) {
                  console.warn("[App] failed to ack stale tournament event:", ackError);
                }
                processedTournamentEventIdsRef.current.add(eventId);
                return;
              }

              const notification = buildTournamentEventNotification({
                ...eventData,
                tournamentName: eventData.tournamentName || effectiveUserProfile?.tournamentName || "",
              });

              if (!notification) {
                return;
              }

              const ackRef = doc(
                db,
                "tournaments",
                activeTournamentDocId,
                "events",
                eventId,
                "sentToAnglers",
                authUser.uid
              );
              const ackSnap = await getDoc(ackRef);
              if (ackSnap.exists()) {
                processedTournamentEventIdsRef.current.add(eventId);
                return;
              }

              // Silently ack events that are too old to be relevant (e.g. day-old
              // reminders surfaced on first snapshot after app reload) so they don't
              // trigger a barrage of audible dings on login. Recent events still display
              // even if delivered slightly after the listener attached.
              const createdAtMs = (() => {
                const raw = eventData.createdAt;
                if (!raw) return 0;
                if (typeof raw.toMillis === "function") return raw.toMillis();
                if (typeof raw.seconds === "number") return raw.seconds * 1000;
                const d = new Date(raw);
                return Number.isNaN(d.getTime()) ? 0 : d.getTime();
              })();
              const HISTORICAL_EVENT_AGE_MS = 10 * 60 * 1000;
              const isHistorical = createdAtMs > 0 && Date.now() - createdAtMs > HISTORICAL_EVENT_AGE_MS;
              if (isHistorical) {
                try {
                  await setDoc(
                    ackRef,
                    {
                      anglerId: authUser.uid,
                      eventId,
                      eventType: notification.eventType,
                      tournamentId: activeTournamentDocId,
                      sentAt: serverTimestamp(),
                      channel: "suppressed-historical",
                      notificationPermission: "n/a",
                    },
                    { merge: true }
                  );
                } catch (ackError) {
                  console.warn("[App] failed to ack historical tournament event:", ackError);
                }
                processedTournamentEventIdsRef.current.add(eventId);
                return;
              }

              setTournamentNoticeQueue((current) => [
                ...current,
                {
                  id: `${eventId}-${Date.now()}`,
                  eventId,
                  title: notification.title,
                  message: notification.message,
                  tone: notification.tone,
                  navigateTo: notification.navigateTo,
                },
              ]);

              triggerTournamentAlertFeedback({
                eventType: notification.eventType,
                sound: soundAlertsRef.current,
                vibrate: vibrationAlertsRef.current,
              });

              const nativeResult = await showTournamentNativeNotification({
                title: notification.title,
                message: notification.message,
                tag: `tournament-event-${activeTournamentDocId}-${eventId}`,
              });

              await setDoc(
                ackRef,
                {
                  anglerId: authUser.uid,
                  eventId,
                  eventType: notification.eventType,
                  tournamentId: activeTournamentDocId,
                  sentAt: serverTimestamp(),
                  channel: nativeResult.shown ? "native+in-app" : "in-app",
                  notificationPermission: nativeResult.permission,
                },
                { merge: true }
              );

              processedTournamentEventIdsRef.current.add(eventId);
            } catch (error) {
              console.warn("[App] tournament event notification failed:", error);
            } finally {
              processingTournamentEventIdsRef.current.delete(eventId);
            }
          })();
        }
      },
      (error) => {
        console.warn("[App] tournament events listener failed:", error);
      }
    );

    return () => unsub();
  }, [activeTournamentDocId, authUser?.uid, effectiveProfileLoading, effectiveUserProfile?.role, effectiveUserProfile?.tournamentName]);

  useEffect(() => {
    const teamId = String(effectiveUserProfile?.teamId || "").trim();
    if (!authUser?.uid || !teamId || effectiveProfileLoading) {
      return undefined;
    }

    const teamListenerAttachedAtMs = Date.now();

    const teamEventsUnsub = onSnapshot(
      collection(db, "teams", teamId, "events"),
      (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type !== "added") return;

          const eventId = String(change.doc.id || "").trim();
          if (!eventId) return;
          if (
            processedTeamEventIdsRef.current.has(eventId) ||
            processingTeamEventIdsRef.current.has(eventId)
          ) {
            return;
          }

          processingTeamEventIdsRef.current.add(eventId);

          (async () => {
            try {
              const eventData = change.doc.data() || {};

              // Skip if already acked by this angler
              if (eventData.sentToAnglers?.[authUser.uid]) {
                processedTeamEventIdsRef.current.add(eventId);
                return;
              }

              const rawEventType = String(eventData.type || eventData.eventType || "").trim();
              const tournamentEndMs = activeTournamentEndMsRef.current;
              const tournamentHasEnded =
                typeof tournamentEndMs === "number" && tournamentEndMs > 0 && Date.now() > tournamentEndMs;

              // Suppress stale time-based reminders once the tournament is over.
              if (
                tournamentHasEnded &&
                (rawEventType === "tournament_starting_soon" ||
                  rawEventType === "tournament_ending_soon" ||
                  rawEventType === "tournament_before_start" ||
                  rawEventType === "tournament_before_end")
              ) {
                try {
                  await updateDoc(doc(db, "teams", teamId, "events", eventId), {
                    [`sentToAnglers.${authUser.uid}`]: {
                      sentAt: serverTimestamp(),
                      ackedAt: serverTimestamp(),
                      channel: "suppressed-stale",
                      notificationPermission: "n/a",
                    },
                  });
                } catch (ackError) {
                  console.warn("[App] failed to ack stale team event:", ackError);
                }
                processedTeamEventIdsRef.current.add(eventId);
                return;
              }

              const notification = buildTournamentEventNotification({
                ...eventData,
                teamName:
                  eventData.teamName ||
                  effectiveUserProfile?.teamName ||
                  "",
              });

              if (!notification) {
                return;
              }

              // Silently ack team events that are too old to be relevant so they don't
              // trigger a barrage of audible dings on login / reload. Recent events still
              // display even if delivered slightly after the listener attached.
              const createdAtMs = (() => {
                const raw = eventData.createdAt;
                if (!raw) return 0;
                if (typeof raw.toMillis === "function") return raw.toMillis();
                if (typeof raw.seconds === "number") return raw.seconds * 1000;
                const d = new Date(raw);
                return Number.isNaN(d.getTime()) ? 0 : d.getTime();
              })();
              const HISTORICAL_TEAM_EVENT_AGE_MS = 10 * 60 * 1000;
              const isHistorical =
                createdAtMs > 0 && Date.now() - createdAtMs > HISTORICAL_TEAM_EVENT_AGE_MS;
              if (isHistorical) {
                try {
                  await updateDoc(doc(db, "teams", teamId, "events", eventId), {
                    [`sentToAnglers.${authUser.uid}`]: {
                      sentAt: serverTimestamp(),
                      ackedAt: serverTimestamp(),
                      channel: "suppressed-historical",
                      notificationPermission: "n/a",
                    },
                  });
                } catch (ackError) {
                  console.warn("[App] failed to ack historical team event:", ackError);
                }
                processedTeamEventIdsRef.current.add(eventId);
                return;
              }

              setTournamentNoticeQueue((current) => [
                ...current,
                {
                  id: `team-${eventId}-${Date.now()}`,
                  eventId,
                  title: notification.title,
                  message: notification.message,
                  tone: notification.tone,
                  navigateTo: notification.navigateTo,
                },
              ]);

              triggerTournamentAlertFeedback({
                eventType: notification.eventType,
                sound: soundAlertsRef.current,
                vibrate: vibrationAlertsRef.current,
              });

              const nativeResult = await showTournamentNativeNotification({
                title: notification.title,
                message: notification.message,
                tag: `team-event-${teamId}-${eventId}`,
              });

              await updateDoc(doc(db, "teams", teamId, "events", eventId), {
                [`sentToAnglers.${authUser.uid}`]: {
                  sentAt: serverTimestamp(),
                  ackedAt: serverTimestamp(),
                  channel: nativeResult.shown ? "native+in-app" : "in-app",
                  notificationPermission: nativeResult.permission,
                },
              });

              processedTeamEventIdsRef.current.add(eventId);
            } catch (error) {
              console.warn("[App] team event notification failed:", error);
            } finally {
              processingTeamEventIdsRef.current.delete(eventId);
            }
          })();
        });
      },
      (error) => {
        console.warn("[App] team events listener failed:", error);
      }
    );

    return () => teamEventsUnsub();
  }, [effectiveUserProfile?.teamId, effectiveUserProfile?.teamName, authUser?.uid, effectiveProfileLoading]);

  // Per-angler tournament events fan-out: backend writes one doc per angler to
  // users/{uid}/events for tournament_starting_soon, tournament_ending_soon,
  // tournament_ended (and others). Dedup is handled server-side via
  // users/{uid}/tournamentNotifications/{tournamentId}; here we just render
  // the banner + audio + native push on first sight and ack the doc.
  useEffect(() => {
    userProfileFallbackRef.current = {
      tournamentName: effectiveUserProfile?.tournamentName || "",
      teamName: effectiveUserProfile?.teamName || "",
    };
  }, [effectiveUserProfile?.tournamentName, effectiveUserProfile?.teamName]);

  useEffect(() => {
    if (!authUser?.uid || effectiveProfileLoading) {
      console.log("[App] user events listener: not attaching (auth/profile not ready)", {
        hasUid: !!authUser?.uid,
        effectiveProfileLoading,
      });
      return undefined;
    }

    const uid = authUser.uid;
    console.log("[App] user events listener: attaching for uid", uid);

    let cancelled = false;
    let currentUnsub = null;
    let retryTimer = null;
    let permissionRetries = 0;
    const MAX_EVENT_LISTENER_RETRIES = 5;

    const attach = () => {
      if (cancelled) return;
      currentUnsub = onSnapshot(
      collection(db, "users", uid, "events"),
      (snap) => {
        permissionRetries = 0;
        console.log("[App] user events snapshot:", {
          size: snap.size,
          changes: snap.docChanges().length,
        });

        // Recompute "tournament is live" state from the full snapshot every
        // time. For each tournament referenced in the events, the latest
        // tournament_starting_soon event determines a target start time; if
        // there's also a tournament_ended event with a later createdAt, the
        // tournament is no longer live.
        const livePerTournament = {};
        const endedPerTournament = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          const rawType = String(data.type || data.eventType || "").trim();
          const tid = String(
            data.metadata?.tournamentId || data.tournamentId || ""
          ).trim();
          if (!tid) return;
          const createdMs =
            typeof data.createdAt?.toMillis === "function"
              ? data.createdAt.toMillis()
              : typeof data.createdAt?.seconds === "number"
              ? data.createdAt.seconds * 1000
              : 0;
          if (
            rawType === "tournament_before_start" ||
            rawType === "tournament_starting_soon"
          ) {
            const startMs =
              Number(data.metadata?.startTimeMs) ||
              Number(data.metadata?.startTime) ||
              (createdMs ? createdMs + 15 * 60 * 1000 : 0);
            if (
              startMs &&
              (!livePerTournament[tid] || startMs > livePerTournament[tid].startMs)
            ) {
              livePerTournament[tid] = { startMs, eventCreatedMs: createdMs };
            }
          } else if (
            rawType === "tournament_at_end" ||
            rawType === "tournament_after_end" ||
            rawType === "tournament_ended"
          ) {
            if (
              !endedPerTournament[tid] ||
              createdMs > endedPerTournament[tid]
            ) {
              endedPerTournament[tid] = createdMs;
            }
          }
        });
        const nextLiveMap = {};
        Object.entries(livePerTournament).forEach(([tid, info]) => {
          const endMs = endedPerTournament[tid] || 0;
          if (endMs && endMs > info.eventCreatedMs) return; // ended after start
          nextLiveMap[tid] = info.startMs;
        });
        setTournamentLiveMap((current) => {
          const sameKeys =
            Object.keys(current).length === Object.keys(nextLiveMap).length &&
            Object.keys(nextLiveMap).every(
              (k) => current[k] === nextLiveMap[k]
            );
          return sameKeys ? current : nextLiveMap;
        });

        snap.docChanges().forEach((change) => {
          console.log("[App] user event change:", change.type, change.doc.id, change.doc.data());
          if (change.type !== "added" && change.type !== "modified") return;

          const eventId = String(change.doc.id || "").trim();
          if (!eventId) return;
          if (
            processedUserEventIdsRef.current.has(eventId) ||
            processingUserEventIdsRef.current.has(eventId)
          ) {
            return;
          }

          processingUserEventIdsRef.current.add(eventId);

          (async () => {
            try {
              const eventData = change.doc.data() || {};

              // Skip if already acked (set by this client previously).
              if (eventData.ackedAt) {
                processedUserEventIdsRef.current.add(eventId);
                return;
              }

              const notification = buildTournamentEventNotification({
                ...eventData,
                tournamentName:
                  eventData.tournamentName ||
                  userProfileFallbackRef.current.tournamentName ||
                  "",
                teamName:
                  eventData.teamName ||
                  userProfileFallbackRef.current.teamName ||
                  "",
              });

              console.log("[App] user event built notification:", {
                eventId,
                rawType: eventData.type || eventData.eventType,
                notification,
              });

              if (!notification) {
                processedUserEventIdsRef.current.add(eventId);
                return;
              }

              // No age-based suppression here: backend already dedupes per
              // tournament per user via users/{uid}/tournamentNotifications and
              // we short-circuit above on eventData.ackedAt. Surface any
              // unacked event regardless of how long ago it was written so
              // late reminders (e.g. T-15 fired while the app was closed)
              // still display the next time the angler opens the app.

              setTournamentNoticeQueue((current) => [
                ...current,
                {
                  id: `user-${eventId}-${Date.now()}`,
                  eventId,
                  title: notification.title,
                  message: notification.message,
                  tone: notification.tone,
                  navigateTo: notification.navigateTo,
                  countdownToMs: notification.countdownToMs || null,
                },
              ]);

              triggerTournamentAlertFeedback({
                eventType: notification.eventType,
                sound: soundAlertsRef.current,
                vibrate: vibrationAlertsRef.current,
              });

              const nativeResult = await showTournamentNativeNotification({
                title: notification.title,
                message: notification.message,
                tag: `user-event-${uid}-${eventId}`,
              });

              try {
                await setDoc(
                  doc(db, "users", uid, "events", eventId),
                  {
                    ackedAt: serverTimestamp(),
                    channel: nativeResult.shown ? "native+in-app" : "in-app",
                    notificationPermission: nativeResult.permission,
                  },
                  { merge: true }
                );
              } catch (ackError) {
                console.warn("[App] failed to ack user event:", ackError);
              }

              processedUserEventIdsRef.current.add(eventId);
            } catch (error) {
              console.warn("[App] user event notification failed:", error);
            } finally {
              processingUserEventIdsRef.current.delete(eventId);
            }
          })();
        });
      },
      (error) => {
        // Right after a fresh sign-in / enrollment the auth credential can
        // briefly lag the Firestore backend, terminating this listen with a
        // transient permission-denied. Unlike one-shot reads, a terminated
        // listener does not re-attach itself, so refresh the token and retry
        // with backoff a bounded number of times before giving up.
        const isPermDenied =
          error?.code === "permission-denied" ||
          /insufficient permissions/i.test(error?.message || "");
        if (isPermDenied && permissionRetries < MAX_EVENT_LISTENER_RETRIES && !cancelled) {
          permissionRetries += 1;
          console.warn(
            `[App] user events listener permission-denied — refreshing token & retrying ` +
              `(${permissionRetries}/${MAX_EVENT_LISTENER_RETRIES})`
          );
          try {
            currentUnsub && currentUnsub();
          } catch {}
          currentUnsub = null;
          (async () => {
            try {
              await auth.currentUser?.getIdToken(true);
            } catch {}
            if (cancelled) return;
            retryTimer = setTimeout(attach, 1200 * permissionRetries);
          })();
          return;
        }
        console.warn("[App] user events listener failed:", error);
      }
    );
    };

    attach();

    return () => {
      console.log("[App] user events listener: detaching for uid", uid);
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      try {
        currentUnsub && currentUnsub();
      } catch {}
    };
  }, [
    authUser?.uid,
    effectiveProfileLoading,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem("the99.showGps", JSON.stringify(showGps));
    } catch {}
  }, [showGps]);

  useEffect(() => {
    try {
      localStorage.setItem("the99.showConditions", JSON.stringify(showConditions));
    } catch {}
  }, [showConditions]);

  useEffect(() => {
    try {
      localStorage.setItem("the99.showGoProButton", JSON.stringify(showGoProButton));
    } catch {}
  }, [showGoProButton]);

  useEffect(() => {
    try {
      localStorage.setItem("the99.goProMaxClipMins", JSON.stringify(goProMaxClipMins));
    } catch {}
  }, [goProMaxClipMins]);

  useEffect(() => {
    try {
      localStorage.setItem("the99.soundAlerts", JSON.stringify(soundAlerts));
    } catch {}
  }, [soundAlerts]);

  useEffect(() => {
    try {
      localStorage.setItem("the99.vibrationAlerts", JSON.stringify(vibrationAlerts));
    } catch {}
  }, [vibrationAlerts]);

  // Prime Web Audio on the first user gesture so iOS Safari PWAs can play alert tones later.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let primed = false;
    const onGesture = () => {
      if (primed) return;
      primed = true;
      try {
        primeTournamentAlertAudio();
      } catch {}
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      window.removeEventListener("touchstart", onGesture);
    };
    window.addEventListener("pointerdown", onGesture, { passive: true });
    window.addEventListener("keydown", onGesture);
    window.addEventListener("touchstart", onGesture, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      window.removeEventListener("touchstart", onGesture);
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("the99.themeMode", themeMode);
    } catch {}
  }, [themeMode]);

  useEffect(() => {
    try {
      localStorage.setItem("the99.fontSizeMode", fontSizeMode);
    } catch {}
    document.body.classList.toggle("font-large", fontSizeMode === "large");
  }, [fontSizeMode]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (profileLoadTimeoutRef.current) {
        clearTimeout(profileLoadTimeoutRef.current);
        profileLoadTimeoutRef.current = null;
      }
      setAuthUser(user || null);
      setProfileLoading(Boolean(user));
      // Mark the profile as not-yet-loaded for this uid. Crucially, do NOT
      // set this to the new uid here: that would collapse the
      // `loadedProfileUid !== authUser.uid` guard in effectiveProfileLoading
      // and let the app momentarily treat the profile as "loaded" before the
      // Firestore snapshot has delivered. For a first-time OAuth user that
      // window renders the Enrollment screen for a frame (role not written
      // yet) before routing to the dashboard. The profile snapshot effect
      // sets loadedProfileUid to the real uid once data actually arrives.
      setLoadedProfileUid(null);
      setProfileAccessError("");
      if (!user) {
        setUserProfile(null);
      } else {
        try { localStorage.setItem("the99.introSeen", "1"); } catch {}
        // Force an ID-token refresh so the Firestore SDK picks up the latest
        // auth credential. Without this, a stale token can persist across
        // SDK upgrades / offline-persistence rehydration and cause spurious
        // "Missing or insufficient permissions" on the first writes.
        user.getIdToken(true).catch((err) => {
          console.warn("[Auth] token refresh on auth change failed:", err);
        });
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  // Capacitor deep-link bridge. When Android App Links hands us a URL (e.g.
  // a sign-in link tapped from an email), the WebView is launched fresh but
  // `window.location.href` is just `https://localhost/` — the inbound URL
  // arrives via the @capacitor/app `appUrlOpen` event instead. Mirror it
  // into the URL bar so existing handlers (isSignInWithEmailLink, the
  // ActivationScreen, etc.) work without any other changes.
  useEffect(() => {
    let cleanup = () => {};
    let cancelled = false;
    (async () => {
      try {
        const mod = await import("@capacitor/app");
        const CapApp = mod.App;
        if (!CapApp || cancelled) return;
        const handle = await CapApp.addListener("appUrlOpen", (event) => {
          const url = event?.url || "";
          if (!url) return;
          try {
            const parsed = new URL(url);
            const next = parsed.pathname + parsed.search + parsed.hash;
            window.history.replaceState({}, "", next || "/");
            if (isSignInWithEmailLink(auth, url)) {
              // Persist the inbound activation URL before anything mutates the
              // URL bar, so the ActivationScreen always has a valid link to
              // complete sign-in.
              try { localStorage.setItem("the99.activationLink", url); } catch {}
              setIsActivationLink(true);
            }
            try {
              const screenParam = (parsed.searchParams.get("screen") || "").trim().toLowerCase();
              if (screenParam === "report") {
                setPendingDeepLink({
                  screen: "report",
                  sessionId: (parsed.searchParams.get("session") || "").trim() || null,
                  catchId: (parsed.searchParams.get("catch") || "").trim() || null,
                });
              }
            } catch {}
            // Re-capture ?promo= / ?bookToken= from a native deep link now that
            // the inbound URL has been mirrored into the address bar.
            try { capturePromoFromUrl(); } catch {}
          } catch (err) {
            console.warn("[deeplink] failed to parse url", url, err);
          }
        });
        cleanup = () => { try { handle.remove(); } catch {} };
      } catch {
        // @capacitor/app unavailable (web build / plugin missing) — no-op.
      }
    })();
    return () => { cancelled = true; cleanup(); };
  }, []);

  // Scheduled-trip auto-start bridge. When the angler taps the T-0 "Start
  // session" action (or the notification body), the plugin delivers
  // localNotificationActionPerformed even from a cold launch. Flag a pending
  // auto-start and route to the dashboard, which performs the actual start.
  useEffect(() => {
    let cleanup = () => {};
    let cancelled = false;
    (async () => {
      try {
        const mod = await import("@capacitor/local-notifications");
        const LN = mod.LocalNotifications;
        if (!LN?.addListener) return;
        const handle = await LN.addListener("localNotificationActionPerformed", (event) => {
          const extra = event?.notification?.extra || {};
          if (extra?.type === "scheduledTripStart") {
            setPendingAutoStart({ tripId: extra.tripId || null, at: Date.now() });
            setScreen("dashboard");
          }
        });
        if (cancelled) { try { handle.remove(); } catch {} }
        else cleanup = () => { try { handle.remove(); } catch {} };
      } catch {
        // @capacitor/local-notifications unavailable (web build) — no-op.
      }
    })();
    return () => { cancelled = true; cleanup(); };
  }, []);

  // ── Book-QR reader-discount redemption ─────────────────────────────────
  // When a signed-in user has a pending book token (captured from the printed
  // QR's ?bookToken= param), redeem it once. The server verifies the
  // signature, enforces one-per-account + a global cap, and marks the user
  // eligible for the reader discount. Idempotent server-side, so a duplicate
  // call for an already-redeemed user is a harmless no-op.
  useEffect(() => {
    if (!authUser?.uid) return undefined;
    const token = getPendingBookToken();
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await redeemBookToken(token, { attested: true });
        if (!cancelled) clearPendingBookToken();
      } catch (err) {
        // Non-fatal: the discount is applied at dashboard checkout, so a
        // transient failure here doesn't block app use. An invalid/forged or
        // un-attested token is cleared so we don't retry it forever.
        if (!cancelled && (err?.code === "invalid_token" || err?.code === "attestation_required")) {
          clearPendingBookToken();
        }
        console.warn("[book] redeem failed:", err?.code || err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [authUser?.uid]);

  // ── Single-device session enforcement ──────────────────────────────────
  // Only one PWA device may be signed in per user at a time. When this
  // device claims the active slot, any previously-signed-in device will
  // detect the deviceId mismatch and force itself out.
  useEffect(() => {
    if (!authUser?.uid) return undefined;
    let cancelled = false;
    let unsubscribe = () => {};
    (async () => {
      await claimActiveDeviceSession(authUser.uid);
      if (cancelled) return;
      unsubscribe = watchActiveDeviceSession(authUser.uid, async () => {
        setDeviceEvictedNotice({
          title: "Signed out",
          message:
            "This account just signed in on another device. For security, only one device can be active at a time.",
        });
        try {
          await signOut(auth);
        } catch (err) {
          console.warn("[Auth] forced sign-out after device eviction failed:", err);
        }
      });
    })();
    return () => {
      cancelled = true;
      try { unsubscribe(); } catch {}
    };
  }, [authUser?.uid]);

  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  useEffect(() => {
    if (!authUser?.uid) {
      if (profileLoadTimeoutRef.current) {
        clearTimeout(profileLoadTimeoutRef.current);
        profileLoadTimeoutRef.current = null;
      }
      setUserProfile(null);
      setLoadedProfileUid(null);
      setProfileLoading(false);
      setProfileAccessError("");
      return undefined;
    }

    setProfileLoading(true);
    profileLoadTimeoutRef.current = setTimeout(() => {
      setLoadedProfileUid(authUser.uid);
      setProfileLoading(false);
    }, 8000);

    const profileRef = doc(db, "users", authUser.uid);

    // Right after an email-link activation the `users/{uid}` doc is still
    // being created and the freshly-minted auth token may not have propagated
    // to the Firestore backend yet. That window can surface a transient
    // permission-denied on the very first listen. Rather than trapping the
    // user permanently on the "Account Access Problem" screen, force a token
    // refresh and re-attach a few times before giving up.
    const MAX_PERMISSION_RETRIES = 5;
    let unsub = () => {};
    let permissionRetries = 0;
    let retryTimer = null;
    let cancelled = false;

    const attach = () => {
      if (cancelled) return;
      unsub = onSnapshot(
        profileRef,
        (snap) => {
          permissionRetries = 0;
          // Firestore's persistent local cache delivers a cache-first snapshot
          // before the server responds. For a returning, subscribed user whose
          // profile doc isn't in this device's cache yet (fresh install, cleared
          // cache, or a new device), that first snapshot reports the doc as
          // missing (`fromCache` + `!exists`). Treating it as authoritative
          // would drop us into the Enrollment screen for ~½ second until the
          // server snapshot arrives with the real profile — the "new user setup
          // flash" seen at login. Ignore that non-authoritative empty result
          // and keep showing the loading state until the server confirms (the
          // 8s watchdog timeout still guards against a hung load).
          if (!snap.exists() && snap.metadata.fromCache) {
            return;
          }
          if (profileLoadTimeoutRef.current) {
            clearTimeout(profileLoadTimeoutRef.current);
            profileLoadTimeoutRef.current = null;
          }
          setUserProfile(snap.exists() ? snap.data() : null);
          setLoadedProfileUid(authUser.uid);
          setProfileLoading(false);
          setProfileAccessError("");
        },
        async (error) => {
          let cachedProfile = userProfileRef.current;
          if (!cachedProfile) {
            try {
              const cachedSnap = await getDocFromCache(profileRef);
              if (cachedSnap.exists()) {
                cachedProfile = cachedSnap.data();
              }
            } catch {}
          }

          if (cancelled) return;

          if (cachedProfile) {
            if (profileLoadTimeoutRef.current) {
              clearTimeout(profileLoadTimeoutRef.current);
              profileLoadTimeoutRef.current = null;
            }
            setUserProfile(cachedProfile);
            setLoadedProfileUid(authUser.uid);
            setProfileLoading(false);
            setProfileAccessError("");
            return;
          }

          // Transient permission-denied during the activation/token-propagation
          // window: refresh the ID token and retry before surfacing the error.
          if (error?.code === "permission-denied" && permissionRetries < MAX_PERMISSION_RETRIES) {
            permissionRetries += 1;
            try { unsub(); } catch {}
            try {
              if (auth.currentUser) {
                await auth.currentUser.getIdToken(true);
              }
            } catch {}
            if (cancelled) return;
            retryTimer = setTimeout(attach, 1200 * permissionRetries);
            return;
          }

          if (profileLoadTimeoutRef.current) {
            clearTimeout(profileLoadTimeoutRef.current);
            profileLoadTimeoutRef.current = null;
          }
          setUserProfile(null);
          setLoadedProfileUid(authUser.uid);
          setProfileLoading(false);
          setProfileAccessError(
            error?.code === "permission-denied"
              ? "Your account signed in successfully, but this app could not access your profile data. This is usually caused by Firestore permissions on users/{uid}."
              : "Your account signed in successfully, but the app could not load your profile data."
          );
        }
      );
    };

    attach();

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (profileLoadTimeoutRef.current) {
        clearTimeout(profileLoadTimeoutRef.current);
        profileLoadTimeoutRef.current = null;
      }
      try { unsub(); } catch {}
    };
  }, [authUser?.uid]);

  const needsEnrollment = Boolean(authUser && !authLoading && !effectiveProfileLoading && !profileAccessError && !effectiveUserProfile?.role);

  // Apply a pending deep-link (e.g. a "Watch" link from the trip-recap email)
  // once the app shell is ready: navigate to the Trip Report for that session
  // and hand the catch id to ReportScreen so it highlights the right video.
  useEffect(() => {
    if (!pendingDeepLink || pendingDeepLink.screen !== "report") return;
    if (authLoading || !authUser || effectiveProfileLoading || needsEnrollment || profileAccessError) return;
    setReportSessionId(pendingDeepLink.sessionId || null);
    setReportHighlightCatchId(pendingDeepLink.catchId || null);
    setScreen("report");
    setPendingDeepLink(null);
  }, [pendingDeepLink, authLoading, authUser, effectiveProfileLoading, needsEnrollment, profileAccessError]);

  // Anglers reach the tournament leaderboard only while they're actually in an
  // active tournament: a live tournaments/* doc matched their assignment and it
  // hasn't ended or been archived. A stale profile.tournamentId left over from a
  // finished event no longer surfaces the Leaderboard tab.
  const isInActiveTournament = Boolean(
    activeTournamentDocId &&
      activeTournamentStatus !== "ended" &&
      activeTournamentStatus !== "archived"
  );
  // A clip certified after the tournament is cancelled/ended/archived is NOT a
  // tournament submission — uploads resume but aren't tagged or reported.
  const tournamentApplicable = Boolean(
    (effectiveUserProfile?.inTournament || effectiveUserProfile?.tournamentId) &&
      !["ended", "archived", "completed", "cancelled", "canceled"].includes(
        String(activeTournamentStatus || "").toLowerCase()
      )
  );
  // View-only roles (Tournament Director, Spectator, Sponsor) get a locked-down
  // experience: the app routes them straight to the tournament leaderboard and
  // the footer exposes only the Leaderboard + Support icons.
  const isViewOnlyRole =
    effectiveUserProfile?.role === "director" ||
    effectiveUserProfile?.role === "participant" ||
    effectiveUserProfile?.role === "sponsor";
  const isFacebookLoginUser = useMemo(() => {
    const providers = (authUser?.providerData || []).map((provider) => provider?.providerId).filter(Boolean);
    const profileProvider = String(effectiveUserProfile?.loginProvider || "").trim();
    let currentLoginProvider = "";
    try {
      currentLoginProvider = String(localStorage.getItem("the99.currentLoginProvider") || "").trim();
    } catch {}

    if (currentLoginProvider) {
      return currentLoginProvider === "facebook.com";
    }
    if (!providers.length) {
      return profileProvider === "facebook.com";
    }
    if (!providers.includes("facebook.com")) return profileProvider === "facebook.com";
    return true;
  }, [authUser?.providerData, effectiveUserProfile?.loginProvider]);
  const showGlobalThemeToggle = Boolean(
    (!authLoading && !authUser) || (authUser && !effectiveProfileLoading && (needsEnrollment || screen !== "dashboard"))
  );

  useEffect(() => {
    if (!authUser?.uid || effectiveProfileLoading) {
      launchGeofenceEventRef.current = "";
      launchGeofenceInsideRef.current = null;
      return;
    }

    const tournamentId = String(effectiveUserProfile?.tournamentId || "").trim();
    const tournamentName = String(effectiveUserProfile?.tournamentName || "").trim();
    const teamId = String(effectiveUserProfile?.teamId || "").trim();
    const teamName = String(effectiveUserProfile?.teamName || "").trim();
    const hasTournament = Boolean(tournamentId || tournamentName);
    const hasLocation = Boolean(location && Number.isFinite(location.lat) && Number.isFinite(location.lng));
    const identityKey = [authUser.uid, tournamentId || tournamentName, teamId].join("|");

    if (!hasTournament || !tournamentAreaRestricted || !hasLocation || isOffline) {
      return;
    }

    // Only police the boundary while the tournament is currently active
    // (between start and end). Outside that window we should not toast or
    // emit a breach event.
    const startMs =
      typeof activeTournamentDates?.startDate === "number" ? activeTournamentDates.startDate : null;
    const endRawMs =
      typeof activeTournamentDates?.endDate === "number" ? activeTournamentDates.endDate : null;
    let endMs = endRawMs;
    if (endRawMs) {
      const d = new Date(endRawMs);
      const isMidnightUtc =
        d.getUTCHours() === 0 && d.getUTCMinutes() === 0 &&
        d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
      if (isMidnightUtc) endMs = endRawMs + 24 * 60 * 60 * 1000 - 1;
    }
    const nowMs = Date.now();
    const tournamentLive =
      (startMs == null || nowMs >= startMs) && (endMs == null || nowMs <= endMs);
    if (!tournamentLive) {
      launchGeofenceInsideRef.current = null;
      return;
    }

    // Reset transition tracking when the angler's tournament/team identity changes.
    if (launchGeofenceEventRef.current !== identityKey) {
      launchGeofenceInsideRef.current = null;
      launchGeofenceEventRef.current = identityKey;
    }

    const inside = isLocationInsideGeofence(location, activeTournamentGeofence);
    const previouslyInside = launchGeofenceInsideRef.current;
    launchGeofenceInsideRef.current = inside;

    if (inside) {
      return;
    }

    // Only fire on a fresh inside->outside transition (or first observation while
    // outside). Subsequent GPS updates while still outside should not re-toast.
    if (previouslyInside === false) {
      return;
    }

    triggerTournamentAlertFeedback({
      eventType: "geofence_breach",
      sound: soundAlertsRef.current,
      vibrate: vibrationAlertsRef.current,
    });
    setTournamentNoticeQueue((current) => [
      ...current,
      {
        id: `launch-geofence-${identityKey}-${Date.now()}`,
        eventId: `launch-geofence-${identityKey}-${Date.now()}`,
        title: "Outside Tournament Boundary",
        message: `You are outside the approved tournament area for ${tournamentName || "your tournament"}. Return inside the boundary to continue.`,
        tone: "danger",
        navigateTo: { screen: "tournament" },
      },
    ]);
    sendTournamentGeofenceAlert({
      tournamentId,
      tournamentName,
      teamId,
      teamName,
      userId: authUser.uid,
      sessionId: "",
      location: { lat: location.lat, lng: location.lng },
      source: previouslyInside === true ? "app-boundary-exit" : "app-launch-immediate",
    }).catch((error) => {
      console.warn("[App] immediate launch geofence event failed:", error);
    });
  }, [
    activeTournamentGeofence,
    activeTournamentDates?.startDate,
    activeTournamentDates?.endDate,
    authUser?.uid,
    effectiveProfileLoading,
    effectiveUserProfile?.teamId,
    effectiveUserProfile?.teamName,
    effectiveUserProfile?.tournamentId,
    effectiveUserProfile?.tournamentName,
    isOffline,
    location,
    tournamentAreaRestricted,
  ]);

  // ── Tournament session GPS trail writer ─────────────────────────────────
  // While a tournament session is live, append a lightweight breadcrumb every
  // ~30s to users/{uid}/sessions/{sid}/trackPoints so the dashboard can replay
  // where a team traveled and resolve boundary disputes. Owner-only write
  // (isSelf covers users/{uid}/**), so no security-rules change is required.
  // Uses a ref for the live position so frequent GPS updates don't restart the
  // interval. The in-flight sessionId is read from the localStorage mirror that
  // SessionContext maintains (this shell is mounted above the provider).
  const trailLocationRef = useRef(null);
  useEffect(() => {
    trailLocationRef.current = location;
  }, [location]);

  useEffect(() => {
    const uid = authUser?.uid;
    if (!uid || effectiveProfileLoading) return undefined;

    const tournamentId = String(effectiveUserProfile?.tournamentId || "").trim();
    const tournamentName = String(effectiveUserProfile?.tournamentName || "").trim();
    const teamId = String(effectiveUserProfile?.teamId || "").trim();
    if (!tournamentId && !tournamentName) return undefined;

    const startMs =
      typeof activeTournamentDates?.startDate === "number" ? activeTournamentDates.startDate : null;
    const endRawMs =
      typeof activeTournamentDates?.endDate === "number" ? activeTournamentDates.endDate : null;

    // Mirror the launch-geofence effect's end-of-day handling: a midnight-UTC
    // end date means "through the end of that day".
    const isLiveNow = () => {
      const now = Date.now();
      let endMs = endRawMs;
      if (endRawMs) {
        const d = new Date(endRawMs);
        const isMidnightUtc =
          d.getUTCHours() === 0 && d.getUTCMinutes() === 0 &&
          d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
        if (isMidnightUtc) endMs = endRawMs + 24 * 60 * 60 * 1000 - 1;
      }
      return (startMs == null || now >= startMs) && (endMs == null || now <= endMs);
    };

    let lastWrittenMs = 0;
    let lastPoint = null;

    const writePoint = async () => {
      if (isOffline || !isLiveNow()) return;

      // Resolve the in-flight session from the localStorage mirror.
      let sessionId = "";
      try {
        const raw = window.localStorage.getItem(`the99.activeSession.${uid}`);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.sessionId) sessionId = String(parsed.sessionId);
      } catch {
        /* non-fatal */
      }
      if (!sessionId) return;

      const loc = trailLocationRef.current;
      if (!loc || !Number.isFinite(loc.lat) || !Number.isFinite(loc.lng)) return;

      // Cut write volume: skip a point unless the boat has moved a meaningful
      // distance or it's been a while since the last recorded fix.
      const nowMs = Date.now();
      if (lastPoint) {
        const moved = trailMetersBetween(lastPoint, loc) >= TRAIL_MIN_MOVE_METERS;
        const stale = nowMs - lastWrittenMs >= TRAIL_MAX_GAP_MS;
        if (!moved && !stale) return;
      }

      const inside = hasTournamentAreaRestriction(activeTournamentGeofence)
        ? isLocationInsideGeofence(loc, activeTournamentGeofence)
        : null;

      const pointId = String(nowMs);
      try {
        await authedWrite(
          () =>
            setDoc(
              doc(db, "users", uid, "sessions", sessionId, "trackPoints", pointId),
              {
                lat: loc.lat,
                lng: loc.lng,
                t: serverTimestamp(),
                clientMs: nowMs,
                speed: Number.isFinite(loc.speed) ? loc.speed : null,
                heading: Number.isFinite(loc.heading) ? loc.heading : null,
                accuracy: Number.isFinite(loc.accuracy) ? loc.accuracy : null,
                inBounds: inside,
                tournamentId: tournamentId || null,
                teamId: teamId || null,
              }
            ),
          `trackPoint users/${uid}/sessions/${sessionId}`
        );
        lastWrittenMs = nowMs;
        lastPoint = { lat: loc.lat, lng: loc.lng };
      } catch (err) {
        console.warn("[App] trackPoint write failed:", err);
      }
    };

    writePoint();
    const interval = setInterval(writePoint, TRAIL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [
    authUser?.uid,
    effectiveProfileLoading,
    effectiveUserProfile?.tournamentId,
    effectiveUserProfile?.tournamentName,
    effectiveUserProfile?.teamId,
    activeTournamentDates?.startDate,
    activeTournamentDates?.endDate,
    activeTournamentGeofence,
    isOffline,
  ]);

  // Best-effort active Session ID for support emails: prefer the in-flight
  // session (persisted to localStorage by SessionContext), falling back to the
  // session currently open in the Trip Report. Recomputed on screen change.
  const supportSessionId = useMemo(() => {
    let activeId = "";
    if (authUser?.uid && typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem(`the99.activeSession.${authUser.uid}`);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && parsed.sessionId) activeId = String(parsed.sessionId);
      } catch {
        /* non-fatal */
      }
    }
    return activeId || reportSessionId || "";
  }, [authUser?.uid, screen, reportSessionId]);

  // In-app support flow: capture a screenshot of the current view BEFORE the
  // modal opens (so the modal isn't in the shot), then surface the sheet.
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportShot, setSupportShot] = useState(null);
  const [supportPreparing, setSupportPreparing] = useState(false);
  const supportContext = authUser ? `App · ${screen}` : "Sign In";
  // "What's New" release-notes sheet. Shown once when a signed-in user lands on
  // the dashboard after a deploy that carries curated changelog entries.
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewEntries, setWhatsNewEntries] = useState([]);
  const whatsNewCheckedRef = useRef(false);
  // Cache & sync status sheet, opened by tapping the offline/sync banner.
  const [cacheStatusOpen, setCacheStatusOpen] = useState(false);
  const openSupport = async () => {
    if (supportPreparing) return;
    setSupportPreparing(true);
    let shot = null;
    try {
      shot = await captureScreenshot();
    } catch {
      /* screenshot is best-effort */
    }
    setSupportShot(shot);
    setSupportOpen(true);
    setSupportPreparing(false);
  };
  const navTabs = isViewOnlyRole
    ? [
        // View-only roles (Director / Spectator / Sponsor) only get the
        // Leaderboard and Support icons — everything else is hidden.
        { id: "tournament", icon: "♛", label: "Leaderboard" },
        { id: "support", icon: <MailIcon size={14} />, label: "Support" },
      ]
    : [
        { id: "dashboard", icon: "◈", label: "Prep Dashboard" },
        // Anglers only see the Leaderboard while they're actively in a
        // tournament (a live tournaments/* doc matched, not ended/archived).
        // Uses the same ♛ icon as the view-only roles' Leaderboard tab.
        ...(isInActiveTournament ? [{ id: "tournament", icon: "♛", label: "Leaderboard" }] : []),
        { id: "recording", icon: "●", label: "Live Recording" },
        { id: "report", icon: "◎", label: "Trip Report" },
        { id: "patterns", icon: "◉", label: "Patterns" },
        { id: "cached", icon: "⤓", label: "Cached", badge: cacheItems.length },
        {
          id: "support",
          icon: <MailIcon size={14} />,
          label: "Support",
        },
      ];

  useEffect(() => {
    if (screen !== "tournament") return;
    if (isViewOnlyRole) return; // view-only roles are locked to the leaderboard
    if (!isInActiveTournament) setScreen("dashboard");
  }, [isViewOnlyRole, isInActiveTournament, screen]);

  useEffect(() => {
    // View-only roles (Tournament Director / Spectator / Sponsor) are locked to
    // the Leaderboard + Support surfaces. If anything routes them elsewhere — a
    // stale default, a deep link, or a race before the role finished loading —
    // bounce them straight back to the tournament leaderboard.
    if (isViewOnlyRole && screen !== "tournament" && screen !== "support") {
      setScreen("tournament");
    }
  }, [isViewOnlyRole, screen]);

  useEffect(() => {
    if (!authUser?.uid) {
      autoRoutedUidRef.current = null;
      return;
    }
    if (effectiveProfileLoading || needsEnrollment || profileAccessError) {
      return;
    }
    if (autoRoutedUidRef.current === authUser.uid) {
      return;
    }

    // A pending deep-link (e.g. a "Watch" link from the trip-recap email)
    // takes priority over the default landing screen. Mark this uid as routed
    // and let the deep-link effect navigate to the Trip Report so we don't
    // clobber it back to the dashboard.
    if (pendingDeepLink?.screen === "report") {
      autoRoutedUidRef.current = authUser.uid;
      return;
    }

    setScreen(isViewOnlyRole ? "tournament" : "dashboard");
    autoRoutedUidRef.current = authUser.uid;
  }, [authUser?.uid, effectiveProfileLoading, needsEnrollment, effectiveUserProfile?.role, profileAccessError, pendingDeepLink]);

  useEffect(() => {
    // Recover the session start time after a full app reload mid-trip: the
    // active-session snapshot persisted by SessionContext carries the original
    // startedAtMs, so the timer resumes at the correct elapsed value instead of
    // restarting from zero.
    if (!authUser?.uid || typeof window === "undefined") return;
    if (sessionStartMs != null) return;
    try {
      const raw = window.localStorage.getItem(`the99.activeSession.${authUser.uid}`);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed?.sessionId && typeof parsed.startedAtMs === "number") {
        setSessionStartMs(parsed.startedAtMs);
        setHasStartedTrip(true);
        // Reading a persisted session on mount means we're picking up a trip
        // that was already in progress before this app load / login.
        setSessionResumed(true);
      }
    } catch {
      /* non-fatal */
    }
  }, [authUser?.uid, sessionStartMs]);

  useEffect(() => {
    // Bridge from SessionContext, which is mounted below us and owns orphan
    // recovery. When it rehydrates an incorrectly-ended session (e.g. the app
    // was force-quit before the localStorage mirror was written, so it can only
    // be recovered asynchronously from Firestore), it persists the ORIGINAL
    // startedAtMs and fires this event. Adopt it so the timer resumes at the
    // real elapsed time since the trip began instead of restarting from zero.
    if (typeof window === "undefined") return undefined;
    const onActiveSession = (e) => {
      const startedAtMs = e?.detail?.startedAtMs;
      if (typeof startedAtMs === "number" && Number.isFinite(startedAtMs)) {
        setSessionStartMs(startedAtMs);
        setHasStartedTrip(true);
        // SessionContext flags whether this was a rehydrated previous trip
        // (orphan recovery) or a brand-new session, so we only show the
        // "resumed" label for genuine resumes.
        setSessionResumed(Boolean(e?.detail?.resumed));
      }
    };
    window.addEventListener("the99:activeSession", onActiveSession);
    return () => window.removeEventListener("the99:activeSession", onActiveSession);
  }, []);

  useEffect(() => {
    // tracks the real trip duration regardless of which screen is mounted. It
    // runs for the whole life of the session (start → end), not just while the
    // recording screen is visible.
    if (sessionStartMs == null) {
      clearInterval(timerRef.current);
      setElapsed(0);
      setSessionResumed(false);
      return undefined;
    }
    const tick = () => {
      setElapsed(Math.max(0, Math.floor((Date.now() - sessionStartMs) / 1000)));
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [sessionStartMs]);

  useEffect(() => {
    // Ensure we stop the camera when not in recording mode.
    if (!isRecording) {
      recorder.stop();
    }
  }, [isRecording, recorder]);

  useEffect(() => {
    const onResize = () => {
      setIsMobileWidth(window.innerWidth <= 480);
      const vvHeight = window.visualViewport?.height || window.innerHeight || 800;
      setViewportHeight(Math.round(vvHeight));
      // The on-screen keyboard shrinks the visual viewport well below the
      // layout viewport. Flag it so we can hide the floating support button,
      // which would otherwise ride up on top of the form above the keyboard.
      setKeyboardOpen((window.innerHeight || vvHeight) - vvHeight > 150);
    };

    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    onResize();
    return () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, []);

  // Prompt anglers who fish both inshore + offshore to pick today's water, once
  // per calendar day. Offshore defaults to Cached (Offline) Mode + the offshore
  // backdrop; the choice persists until logout.
  useEffect(() => {
    if (!authUser?.uid || effectiveProfileLoading || needsEnrollment || profileAccessError) {
      return;
    }
    const subTypes = Array.isArray(effectiveUserProfile?.subTypes)
      ? effectiveUserProfile.subTypes
      : [];
    const fishesBoth = subTypes.includes("inshore") && subTypes.includes("offshore");
    if (!fishesBoth) {
      setOffshoreToday(false);
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const dedupeKey = `${authUser.uid}:${today}`;
    if (fishingDayCheckedRef.current === dedupeKey) return;
    fishingDayCheckedRef.current = dedupeKey;
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem("the99.fishingDayMode") || "null");
    } catch {}
    if (stored && stored.uid === authUser.uid && stored.date === today) {
      setOffshoreToday(stored.mode === "offshore");
      return;
    }
    setFishingDayPrompt("choose");
  }, [authUser?.uid, effectiveProfileLoading, needsEnrollment, profileAccessError, effectiveUserProfile?.subTypes]);

  const persistFishingDay = (mode) => {
    try {
      localStorage.setItem(
        "the99.fishingDayMode",
        JSON.stringify({ uid: authUser?.uid || "", date: new Date().toISOString().slice(0, 10), mode })
      );
    } catch {}
  };
  const handleChooseInshore = () => {
    persistFishingDay("inshore");
    setOffshoreToday(false);
    setFishingDayPrompt(null);
  };
  const handleChooseOffshore = () => {
    persistFishingDay("offshore");
    setOffshoreToday(true);
    setFishingDayPrompt("cached");
  };
  const handleEnableCachedMode = () => {
    setForceOffline?.(true);
    setFishingDayPrompt(null);
  };
  const handleSkipCachedMode = () => {
    setFishingDayPrompt(null);
  };

  const handleSignOut = async () => {
    try {
      try {
        localStorage.removeItem("the99.currentLoginProvider");
      } catch {}
      await signOut(auth);
      setScreen("dashboard");
      setShowCatch(false);
      setShowVideo(false);
      setHasStartedTrip(false);
      // Reset the day's offshore/cached selection so the next login re-prompts.
      try { localStorage.removeItem("the99.fishingDayMode"); } catch {}
      // Reset the saltwater/freshwater session choice so the next login
      // re-prompts anglers who fish both.
      try { sessionStorage.removeItem("the99.fishingMode"); } catch {}
      fishingDayCheckedRef.current = "";
      setOffshoreToday(false);
      setFishingDayPrompt(null);
      setForceOffline?.(false);
      setVideoTarget(null);
      setReportSessionId(null);
      setSessionStartMs(null);
      setElapsed(0);
    } catch (err) {
      console.error("Sign out failed:", err);
    }
  };

  const handleCancelEnrollment = async () => {
    if (!authUser?.uid) return;
    try {
      await deleteDoc(doc(db, "users", authUser.uid));
    } catch (err) {
      console.error("Cancel enrollment cleanup failed:", err);
    } finally {
      await handleSignOut();
    }
  };

  const handleLeaveTournament = async () => {
    if (!authUser?.uid || !effectiveUserProfile) return;

    setLeavingTournament(true);
    try {
      if (effectiveUserProfile.teamId) {
        await setDoc(
          doc(db, "teams", effectiveUserProfile.teamId),
          {
            memberUserIds: arrayRemove(authUser.uid),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      await setDoc(
        doc(db, "users", authUser.uid),
        {
          inTournament: false,
          tournamentId: "",
          tournamentName: "",
          teamId: "",
          teamName: "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.error("Leave tournament failed:", err);
    } finally {
      setLeavingTournament(false);
    }
  };

  const frameRadius = isMobileWidth ? 24 : 44;
  const hasOAuthProvider = !!authUser && (authUser.providerData || []).some(
    (p) => p?.providerId === "google.com" || p?.providerId === "facebook.com" || p?.providerId === "apple.com"
  );
  const isVerifiedOrGrandfathered = !authUser ? true : (
    authUser.emailVerified
    || hasOAuthProvider
    || !Number.isFinite(Date.parse(authUser.metadata?.creationTime || ""))
    || Date.parse(authUser.metadata?.creationTime || "") < EMAIL_VERIFICATION_GATE_CUTOFF_MS
  );
  // Tournament Directors and Participants don't pay anything in the mobile
  // app — the only TD/Participant feature here is viewing the leaderboard.
  // The dashboard remains the paywall for TDs (per the Stripe webhook); on
  // mobile we let them straight through once enrolled.
  const showBottomNav = !authLoading && !!authUser && !effectiveProfileLoading && !needsEnrollment && !profileAccessError && isVerifiedOrGrandfathered;
  // Support button — always reachable (except mid-recording / initial load) so
  // a user who hits a problem on any screen can email support@the99-app.com.
  // When the footer nav is visible, Support lives there instead, so the
  // floating affordance only appears on screens without the bottom nav.
  // Also suppress it while a restored (persisted) login is still resolving the
  // profile — otherwise it briefly flashes bottom-right before the bottom nav
  // mounts and takes over the Support affordance.
  const showSupportButton = Boolean(
    !authLoading && !isRecording && !showBottomNav && !(authUser && effectiveProfileLoading) && !keyboardOpen
  );
  const appViewportHeight = isMobileWidth ? `${viewportHeight}px` : "100dvh";

  // Detect a new build with curated release notes. Runs once per app load, the
  // first time a signed-in, enrolled user is on the dashboard. First-ever users
  // (no stored version) are silently caught up and shown nothing.
  useEffect(() => {
    if (whatsNewCheckedRef.current) return;
    if (!showBottomNav || screen !== "dashboard") return;
    whatsNewCheckedRef.current = true;
    let lastSeen = null;
    try {
      lastSeen = window.localStorage.getItem("the99.lastSeenChangelog");
    } catch {
      return; // storage unavailable — skip quietly
    }
    if (!lastSeen) {
      try {
        window.localStorage.setItem("the99.lastSeenChangelog", APP_VERSION);
      } catch {
        /* best-effort */
      }
      return;
    }
    const unseen = getUnseenEntries(lastSeen);
    if (unseen.length) {
      setWhatsNewEntries(unseen);
      setWhatsNewOpen(true);
    } else if (lastSeen !== APP_VERSION) {
      try {
        window.localStorage.setItem("the99.lastSeenChangelog", APP_VERSION);
      } catch {
        /* best-effort */
      }
    }
  }, [showBottomNav, screen]);

  const dismissWhatsNew = () => {
    setWhatsNewOpen(false);
    try {
      window.localStorage.setItem("the99.lastSeenChangelog", APP_VERSION);
    } catch {
      /* best-effort */
    }
  };

  return (
    <SessionProvider userId={authUser?.uid || null}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: appViewportHeight,
        minHeight: appViewportHeight,
        paddingTop: isMobileWidth ? 6 : 0,
        paddingLeft: isMobileWidth ? 6 : 0,
        paddingRight: isMobileWidth ? 6 : 0,
        paddingBottom: isMobileWidth ? "max(6px, env(safe-area-inset-bottom))" : 0,
        boxSizing: "border-box",
        background: C.appBackdrop,
        fontFamily: "Calibri,sans-serif",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 375,
          height: "100%",
          maxHeight: isMobileWidth ? "none" : 780,
          background: C.deepWater,
          ...(offshoreToday
            ? {
                backgroundImage: `url("${process.env.PUBLIC_URL || ""}/offshore-scene v.2.png")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }
            : {}),
          borderRadius: frameRadius,
          overflow: "hidden",
          position: "relative",
          boxShadow: isMobileWidth ? "none" : C.frameShadow,
          display: "flex",
          flexDirection: "column",

        }}
      >
        {/* Phone frame border overlay — visible on all screens */}
        <div style={{
          position: "absolute",
          inset: isMobileWidth ? 2 : 3,
          borderRadius: frameRadius - 2,
          border: themeMode === "light" ? "1px solid rgba(43,73,99,0.55)" : "1px solid rgba(255,255,255,0.45)",
          pointerEvents: "none",
          zIndex: 9999,
        }} />
        {showGlobalThemeToggle && (
          <button
            onClick={toggleThemeMode}
            type="button"
            aria-label={`Switch to ${themeMode === "light" ? "dark" : "light"} mode`}
            title={`Switch to ${themeMode === "light" ? "dark" : "light"} mode`}
            style={{
              position: "absolute",
              top: (isOffline || syncing) ? 44 : 12,
              right: 20,
              width: 28,
              height: 28,
              borderRadius: 999,
              border: `1px solid ${C.seafoam}66`,
              background: C.cardBg,
              color: C.white,
              cursor: "pointer",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
            }}
          >
            {themeMode === "light" ? "☾" : "☀"}
          </button>
        )}
        {showSupportButton && (
          <div
            style={{
              position: "absolute",
              bottom: authUser
                ? (showBottomNav ? "calc(60px + env(safe-area-inset-bottom, 0px))" : "calc(14px + env(safe-area-inset-bottom, 0px))")
                : "calc(24px + env(safe-area-inset-bottom, 0px))",
              ...(authUser
                ? { right: 14 }
                : { left: 0, right: 0, display: "flex", justifyContent: "center" }),
              zIndex: 9,
            }}
          >
            <SupportButton
              variant="floating"
              context={authUser ? `App · ${screen}` : "Sign In"}
              userId={authUser?.uid || ""}
              sessionId={supportSessionId}
            />
          </div>
        )}
        <SupportModal
          open={supportOpen}
          onClose={() => setSupportOpen(false)}
          screenshotDataUrl={supportShot}
          context={supportContext}
          sessionId={supportSessionId}
          userId={authUser?.uid || ""}
        />
        <WhatsNewModal
          open={whatsNewOpen}
          entries={whatsNewEntries}
          onClose={dismissWhatsNew}
        />
        <CacheStatusModal
          open={cacheStatusOpen}
          onClose={() => setCacheStatusOpen(false)}
          items={cacheItems}
          activeUpload={activeUpload}
          syncing={syncing}
          isOffline={isOffline}
          lastSyncAt={lastSyncAt}
          onRetry={retryNow}
        />
        {/* "Where are you fishing today?" prompt for anglers who fish both
            inshore + offshore. Offshore defaults to Cached (Offline) Mode. */}
        {fishingDayPrompt && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10001,
              background: "rgba(0,0,0,0.72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 320,
                background: C.cardBg,
                border: `1px solid ${C.border}`,
                borderRadius: 14,
                padding: 20,
              }}
            >
              {fishingDayPrompt === "choose" ? (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.white, marginBottom: 10 }}>
                    Where are you fishing today?
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 18 }}>
                    You fish both inshore and offshore. Let us tune the app for today's trip.
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button variant="secondary" style={{ flex: 1 }} onClick={handleChooseInshore}>
                      Inshore
                    </Button>
                    <Button variant="primary" style={{ flex: 1 }} onClick={handleChooseOffshore}>
                      Offshore
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.white, marginBottom: 10 }}>
                    Heading offshore?
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 18 }}>
                    You'll likely be outside cell tower range. Turn on{" "}
                    <strong style={{ color: C.white }}>Cached Mode</strong> so your catches and videos
                    save to your device and upload automatically when you're back in range.
                    <br />
                    <br />
                    Some boats have satellite network — if you have a connection, you can leave it off.
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button variant="primary" style={{ flex: 1 }} onClick={handleEnableCachedMode}>
                      Turn on Cached Mode
                    </Button>
                    <Button variant="ghost" style={{ flex: 1 }} onClick={handleSkipCachedMode}>
                      Not now
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {/* Promo banner — shown once after sign-in if user arrived via a promo QR/link. */}
        {!!authUser && promoAttribution && !promoBannerDismissed && (
          <PromoBanner
            source={promoAttribution.source}
            onDismiss={() => {
              markBannerSeen(promoAttribution.source);
              setPromoBannerDismissed(true);
            }}
          />
        )}
        {/* Offline banner — only shown when logged in */}
        {!!authUser && (isOffline || syncing) && (
          <div
            onClick={() => setCacheStatusOpen(true)}
            role="button"
            tabIndex={0}
            style={{
            cursor: "pointer",
            background: syncing
              ? "linear-gradient(90deg,#0e3a1e,#1a5c2e)"
              : forceOffline
              ? "linear-gradient(90deg,#0e2a50,#1a3f7a)"
              : "linear-gradient(90deg,#5a1a00,#a03000)",
            color: "#fff",
            fontSize: 11,
            textAlign: "center",
            padding: "7px 12px",
            letterSpacing: 0.8,
            fontWeight: 700,
            zIndex: 9998,
            position: "relative",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            textTransform: "uppercase",
            borderBottom: syncing
              ? "1px solid #2dc97444"
              : forceOffline
              ? "1px solid #4a8fff44"
              : "1px solid #ff6a0044",
          }}>
            <span style={{ fontSize: 13 }}>
              {syncing ? "↺" : forceOffline ? "📵" : "⚠︎"}
            </span>
            <span>
              {syncing
                ? `Syncing ${pendingVideoCount > 0 ? `${pendingVideoCount} video${pendingVideoCount > 1 ? "s" : ""}` : ""}${pendingVideoCount > 0 && pendingCatchCount > 0 ? " & " : ""}${pendingCatchCount > 0 ? `${pendingCatchCount} catch${pendingCatchCount > 1 ? "es" : ""}` : ""}${(pendingVideoCount > 0 || pendingCatchCount > 0) && pendingGeofenceAlertCount > 0 ? " & " : ""}${pendingGeofenceAlertCount > 0 ? `${pendingGeofenceAlertCount} alert${pendingGeofenceAlertCount > 1 ? "s" : ""}` : ""}…`
                : forceOffline
                ? `Offline Mode${(pendingVideoCount + pendingCatchCount + pendingGeofenceAlertCount) > 0 ? ` · ${pendingVideoCount + pendingCatchCount + pendingGeofenceAlertCount} queued` : " · Cached data"}`
                : `No Connection${(pendingVideoCount + pendingCatchCount + pendingGeofenceAlertCount) > 0 ? ` · ${pendingVideoCount + pendingCatchCount + pendingGeofenceAlertCount} queued` : " · Cached data"}`
              }
            </span>
            <span style={{ fontSize: 13, opacity: 0.85 }}>›</span>
          </div>
        )}
        {!!authUser && !!tournamentNotice && (
          <div
            style={{
              position: "relative",
              zIndex: 9997,
              padding: "14px 16px",
              background:
                tournamentNotice.tone === "danger"
                  ? "linear-gradient(90deg,#5f1200,#a32900)"
                  : tournamentNotice.tone === "warning"
                  ? "linear-gradient(90deg,#5d4100,#9b6c00)"
                  : tournamentNotice.tone === "notice"
                  ? "linear-gradient(90deg,#0a2a5e,#1a5fbf)"
                  : "linear-gradient(90deg,#0d3f46,#12736b)",
              borderBottom:
                tournamentNotice.tone === "danger"
                  ? "1px solid rgba(255,124,77,0.35)"
                  : tournamentNotice.tone === "warning"
                  ? "1px solid rgba(255,212,91,0.35)"
                  : tournamentNotice.tone === "notice"
                  ? "1px solid rgba(96,165,250,0.45)"
                  : "1px solid rgba(59,214,197,0.35)",
              color: "#fff",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" }}>
                {tournamentNotice.title}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.45, opacity: 0.96, marginTop: 4 }}>
                {tournamentNotice.message}
              </div>
              {tournamentNotice.countdownToMs ? (
                <div style={{ fontSize: 12, opacity: 0.92, marginTop: 6, letterSpacing: 0.3 }}>
                  Starts in{" "}
                  <NoticeCountdown
                    targetMs={tournamentNotice.countdownToMs}
                    onExpire={() => setTournamentNotice(null)}
                  />
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => {
                setTournamentNotice(null);
              }}
              aria-label="Dismiss"
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: 20,
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: "hidden", display: "flex", flexDirection: "column", position: "relative", boxSizing: "border-box", paddingBottom: showBottomNav ? "calc(60px + env(safe-area-inset-bottom, 0px))" : 0 }}>
          {(authLoading || (authUser && effectiveProfileLoading)) && (
            <div style={{ flex: 1, display: "grid", placeItems: "center", color: C.muted, fontSize: 12 }}>
              Loading...
            </div>
          )}

          {!authLoading && !authUser && !isActivationLink && (
            <div key="auth-shell" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
              {deviceEvictedNotice && (
                <div
                  role="alert"
                  style={{
                    margin: "20px 12px 12px 12px",
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${C.warning}66`,
                    background: "rgba(255,170,80,0.08)",
                    color: C.white,
                    fontSize: 12,
                    lineHeight: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <div style={{ fontWeight: 700, color: C.warning, letterSpacing: 1 }}>
                    {deviceEvictedNotice.title}
                  </div>
                  <div>{deviceEvictedNotice.message}</div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeviceEvictedNotice(null)}
                    style={{ alignSelf: "flex-end" }}
                  >
                    Dismiss
                  </Button>
                </div>
              )}
              <AuthScreen />
            </div>
          )}
          {!authLoading && !authUser && isActivationLink && (
            <div key="activation-shell" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
              <ActivationScreen
                onActivated={() => setIsActivationLink(false)}
                onCancel={() => setIsActivationLink(false)}
              />
            </div>
          )}

          {!authLoading && !!authUser && !isVerifiedOrGrandfathered && (
            <div key={`verify-shell-${authUser.uid}`} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
              <VerifyEmailScreen user={authUser} onSignOut={handleSignOut} />
            </div>
          )}

          {!authLoading && !!authUser && !effectiveProfileLoading && needsEnrollment && isVerifiedOrGrandfathered && (
            <div key={`enroll-shell-${authUser.uid}`} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
              <EnrollmentScreen
                user={authUser}
                profile={userProfile}
                onComplete={() => setScreen("dashboard")}
                onCancel={handleCancelEnrollment}
              />
            </div>
          )}

          {!authLoading && !!authUser && !effectiveProfileLoading && !!profileAccessError && (
            <div key={`profile-access-shell-${authUser.uid}`} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative", padding: "16px 14px 90px" }}>
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 11, color: C.teal, letterSpacing: 3, marginBottom: 4 }}>THE 99</div>
                <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>
                  Account Access Problem
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
                {profileAccessError}
              </div>
              <div style={{ fontSize: 11, color: C.white, marginBottom: 16 }}>
                Signed in as {authUser.email || authUser.uid}
              </div>
              <Button
                variant="secondary"
                fullWidth
                onClick={handleSignOut}
              >
                Sign Out
              </Button>
            </div>
          )}

          {!authLoading && !!authUser && !effectiveProfileLoading && !needsEnrollment && !profileAccessError && isVerifiedOrGrandfathered && showWelcome && (
            <div key={`welcome-shell-${authUser.uid}`} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
              <WelcomeScreen onStart={dismissWelcome} />
            </div>
          )}

          {!authLoading && !!authUser && !effectiveProfileLoading && !needsEnrollment && !profileAccessError && isVerifiedOrGrandfathered && !showWelcome && (
            <div key={`app-shell-${authUser.uid}`} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
              {screen === "dashboard" && !isViewOnlyRole && (

                <DashboardScreen
                  userId={authUser?.uid || ""}
                  onStart={() => {
                    setHasStartedTrip(true);
                    // Preserve the original trip start time when resuming an
                    // in-progress session (recovered from localStorage after an
                    // app restart). Only anchor to "now" for a brand-new trip,
                    // otherwise the timer would reset to 0 instead of continuing
                    // from where the trip actually began.
                    setSessionStartMs((prev) => {
                      if (prev != null) return prev;
                      // Fallback for an async-recovered session whose start
                      // event we may have missed: adopt the persisted start
                      // time so elapsed reflects the real trip duration.
                      try {
                        const raw = window.localStorage.getItem(`the99.activeSession.${authUser?.uid}`);
                        const parsed = raw ? JSON.parse(raw) : null;
                        if (parsed?.sessionId && typeof parsed.startedAtMs === "number") {
                          return parsed.startedAtMs;
                        }
                      } catch {
                        /* non-fatal */
                      }
                      return Date.now();
                    });
                    setScreen("recording");
                  }}
                  onViewTrips={() => setScreen("sessions")}
                  autoStartTrip={pendingAutoStart}
                  onAutoStartConsumed={() => setPendingAutoStart(null)}
                  onOpenGoPro={() => setScreen("gopro")}
                  showGoProButton={showGoProButton}
                  setShowGoProButton={setShowGoProButton}
                  goProMaxClipMins={goProMaxClipMins}
                  setGoProMaxClipMins={setGoProMaxClipMins}
                  onSignOut={handleSignOut}
                  showGps={showGps}
                  setShowGps={setShowGps}
                  showConditions={showConditions}
                  setShowConditions={setShowConditions}
                  soundAlerts={soundAlerts}
                  setSoundAlerts={setSoundAlerts}
                  vibrationAlerts={vibrationAlerts}
                  setVibrationAlerts={setVibrationAlerts}
                  location={location}
                  geoStatus={geoStatus}
                  onRequestGeoPermission={requestGeoPermission}
                  userProfile={effectiveUserProfile}
                  themeMode={themeMode}
                  onThemeModeChange={setThemeMode}
                  fontSizeMode={fontSizeMode}
                  onFontSizeModeChange={setFontSizeMode}
                  onLeaveTournament={handleLeaveTournament}
                  leavingTournament={leavingTournament}
                  isOffline={isOffline}
                  forceOffline={forceOffline}
                  tournamentAreaRestricted={tournamentAreaRestricted}
                  tournamentDates={activeTournamentDates}
                  tournamentStatus={activeTournamentStatus}
                  tournamentLiveStartMs={
                    activeTournamentDocId
                      ? tournamentLiveMap[activeTournamentDocId] || null
                      : null
                  }
                />
              )}
              {screen === "tournament" && (
                <TournamentScreen userId={authUser.uid} profile={effectiveUserProfile} onSignOut={handleSignOut} />
              )}
              {screen === "recording" && (
                <RecordingScreen
                  onCatch={() => { setCatchInitialOutcome("landed"); setShowCatch(true); }}
                  onMissedFish={() => { setCatchInitialOutcome("missed"); setShowCatch(true); }}
                  onBack={() => setScreen("dashboard")}
                  onEnd={(endedSessionId) => {
                    setSessionStartMs(null);
                    setReportSessionId(endedSessionId || null);
                    setScreen("report");
                    // Happy moment: the angler just wrapped up a trip. Ask for a
                    // store rating (native-only, throttled to ~once/quarter).
                    // Delayed so the sheet lands over the trip report rather
                    // than mid screen-transition.
                    if (endedSessionId) {
                      setTimeout(() => { maybePromptForReview(); }, 2500);
                    }
                  }}
                  elapsed={elapsed}
                  sessionResumed={sessionResumed}
                  onVideo={(payload) => {
                    setVideoTarget(payload || null);
                    setShowVideo(true);
                  }}
                  location={location}
                  geoStatus={geoStatus}
                  recorder={recorder}
                  userProfile={effectiveUserProfile}
                  isOffline={isOffline}
                  tournamentGeofence={activeTournamentGeofence}
                  tournamentDates={activeTournamentDates}
                  soundAlerts={soundAlerts}
                  vibrationAlerts={vibrationAlerts}
                />
              )}
              {screen === "sessions" && (
                <SessionsScreen
                  onBack={() => setScreen("dashboard")}
                  onSelectSession={(selectedSessionId) => {
                    setReportSessionId(selectedSessionId || null);
                    setScreen("report");
                  }}
                />
              )}
              {screen === "report" && (
                <ReportScreen
                  sessionId={reportSessionId}
                  highlightCatchId={reportHighlightCatchId}
                  onHighlightConsumed={() => setReportHighlightCatchId(null)}
                  userProfile={effectiveUserProfile}
                  onBack={() => setScreen("dashboard")}
                />
              )}
              {screen === "patterns" && <PatternsScreen />}
              {screen === "cached" && (
                <CachedScreen
                  items={cacheItems}
                  activeUpload={activeUpload}
                  syncing={syncing}
                  isOffline={isOffline}
                  lastSyncAt={lastSyncAt}
                  onRetry={retryNow}
                  onDeleteItem={removeItem}
                  uploadSettings={uploadSettings}
                  onChangeUploadSetting={handleChangeUploadSetting}
                  forceOffline={forceOffline}
                  onToggleForceOffline={setForceOffline}
                  onNavigateHistory={() => setScreen("report")}
                />
              )}
              {screen === "gopro" && (
                <GoProScreen
                  onBack={() => setScreen("dashboard")}
                  userProfile={effectiveUserProfile}
                  location={location}
                  maxClipMins={goProMaxClipMins}
                  tournamentGate={(() => {
                    // A cancelled/ended/archived tournament is over — GoPro
                    // uploads resume (recreational). Otherwise uploads stay gated
                    // to the active start–end window.
                    const status = String(activeTournamentStatus || "").toLowerCase();
                    const terminal = ["archived", "ended", "completed", "cancelled", "canceled"].includes(status);
                    const startMs = typeof activeTournamentDates?.startDate === "number" ? activeTournamentDates.startDate : null;
                    const endMs = typeof activeTournamentDates?.endDate === "number" ? activeTournamentDates.endDate : null;
                    const inTournament = Boolean(effectiveUserProfile?.inTournament || effectiveUserProfile?.tournamentId);
                    return { inTournament, terminal, startMs, endMs, name: effectiveUserProfile?.tournamentName || "" };
                  })()}
                  onCertify={(payload) => {
                    const pc = payload?.pendingCatch || null;
                    // Remember which camera clip is being certified so the logged
                    // catch can be stamped back onto its list row.
                    setActiveGoproClipKey(payload?.clipKey || null);
                    // GoPro clips route through the trim step before certify/upload.
                    setTrimTarget({
                      preloadedVideo: payload,
                      catchData: pc?.catchData || { species: "—", size: "—", weight: "—", lure: "—" },
                      catchId: pc?.catchId || null,
                      sessionId: pc?.sessionId || null,
                    });
                    setShowTrim(true);
                    // Consumed — this clip now owns the staged catch.
                    setGoproPendingCatch(null);
                  }}
                  pendingCatch={goproPendingCatch}
                  onClearPendingCatch={() => setGoproPendingCatch(null)}
                  onLogCatchFirst={() => { setCatchFirstMode(true); setShowCatch(true); }}
                  clipCatchOverride={goproClipCatch}
                  onLogCatch={(certId) => {
                    setScreen("dashboard");
                    if (certId) setPendingCertId(certId);
                    setShowCatch(true);
                  }}
                />
              )}
              {showCatch && <CatchScreen onClose={() => { setShowCatch(false); setPendingCertId(null); setCatchInitialOutcome("landed"); setCatchFirstMode(false); setActiveGoproClipKey(null); }} onSaved={(info) => { if (catchFirstMode && info?.catchId) setGoproPendingCatch(info); if (activeGoproClipKey && info?.catchData) { const cd = info.catchData; setGoproClipCatch({ clipKey: activeGoproClipKey, species: cd.species, size: cd.size, weight: cd.weight, lure: cd.lure, certId: pendingCertId || info.catchId || null }); } }} location={location} pendingCertId={pendingCertId} userProfile={effectiveUserProfile} initialOutcome={catchInitialOutcome} allowedSpecies={(() => {
                const status = String(activeTournamentStatus || "").toLowerCase();
                if (status === "archived" || status === "ended" || status === "completed") return [];
                const endMs = typeof activeTournamentDates?.endDate === "number" ? activeTournamentDates.endDate : null;
                if (endMs && Date.now() > endMs) return [];
                return activeTournamentFishSpecies;
              })()} tournamentName={effectiveUserProfile?.tournamentName || ""} />}
              {showTrim && trimTarget && (
                <TrimScreen
                  preloadedVideo={trimTarget.preloadedVideo}
                  inTournament={Boolean(effectiveUserProfile?.inTournament || effectiveUserProfile?.tournamentId)}
                  tournamentName={effectiveUserProfile?.tournamentName || ""}
                  onCancel={() => {
                    try { trimTarget.preloadedVideo?.onComplete?.(); } catch (_) {}
                    setShowTrim(false);
                    setTrimTarget(null);
                  }}
                  onProceed={({ preloadedVideo, trimMeta }) => {
                    setVideoTarget({
                      preloadedVideo,
                      catchData: trimTarget.catchData,
                      catchId: trimTarget.catchId,
                      sessionId: trimTarget.sessionId,
                      trimMeta: trimMeta || null,
                    });
                    setShowTrim(false);
                    setTrimTarget(null);
                    setShowVideo(true);
                  }}
                />
              )}
              {showVideo && (
                <VideoScreen
                  onClose={() => {
                    try { videoTarget?.preloadedVideo?.onComplete?.(); } catch (_) {}
                    // Catch-first: stamp the known catch onto the GoPro clip row.
                    if (videoTarget?.catchId && videoTarget?.preloadedVideo?.clipKey) {
                      const cd = videoTarget.catchData || {};
                      setGoproClipCatch({ clipKey: videoTarget.preloadedVideo.clipKey, species: cd.species, size: cd.size, weight: cd.weight, lure: cd.lure, certId: null });
                    }
                    setActiveGoproClipKey(null);
                    setShowVideo(false);
                    setVideoTarget(null);
                  }}
                  onBackToGoPro={
                    videoTarget?.preloadedVideo?.source === "gopro"
                      ? () => {
                          try { videoTarget?.preloadedVideo?.onComplete?.(); } catch (_) {}
                          if (videoTarget?.catchId && videoTarget?.preloadedVideo?.clipKey) {
                            const cd = videoTarget.catchData || {};
                            setGoproClipCatch({ clipKey: videoTarget.preloadedVideo.clipKey, species: cd.species, size: cd.size, weight: cd.weight, lure: cd.lure, certId: null });
                          }
                          setActiveGoproClipKey(null);
                          setShowVideo(false);
                          setVideoTarget(null);
                          setScreen("gopro");
                        }
                      : null
                  }
                  onLogCatch={(certId) => {
                    try { videoTarget?.preloadedVideo?.onComplete?.(); } catch (_) {}
                    setShowVideo(false);
                    setVideoTarget(null);
                    if (certId) setPendingCertId(certId);
                    setShowCatch(true);
                  }}
                  location={location}
                  userProfile={effectiveUserProfile}
                  isFacebookLoginUser={isFacebookLoginUser}
                  isOffline={isOffline}
                  catchData={videoTarget?.catchData || { species: "—", size: "—", weight: "—", lure: "—" }}
                  preloadedVideo={videoTarget?.preloadedVideo || null}
                  trimMeta={videoTarget?.trimMeta || null}
                  tournamentApplicable={tournamentApplicable}
                  catchRef={
                    videoTarget
                      ? {
                          catchId: videoTarget.catchId,
                          sessionId: videoTarget.sessionId,
                        }
                      : null
                  }
                />
              )}
            </div>
          )}
        </div>

        {showBottomNav && (
          <BottomNav
            active={screen}
            disabledTargets={hasStartedTrip ? [] : ["recording", "report"]}
            tabs={navTabs}
            onNavigate={(target) => {
              if (target === "support") {
                openSupport();
                return;
              }
              if (!hasStartedTrip && ["recording", "report"].includes(target)) {
                return;
              }
              setScreen(target);
            }}
          />
        )}
      </div>
    </div>
    </SessionProvider>
  );
}

export default App;
